/**
 * WorkspaceView - Full Workspace Mode with Scope Selection, Saved Runs, AI Naming
 *
 * Key Features:
 * - Graph view of COBOL/Java components
 * - Transformation Scope panel (select components for targeted generation)
 * - Saved runs (analysis state management)
 * - Context menu with Include in Scope, Save as Service
 * - AI Naming review panel
 * - Service creation from selected nodes
 *
 * REAL API INTEGRATION:
 * - Uses workspaceService to call real APIs (Code Analysis, Dependency Mapper, etc.)
 * - Falls back to mock data if project not provided or API fails
 * - Works with ANY COBOL application - no hardcoding
 */

import { useState, useCallback, useMemo, useRef, useEffect } from 'react'
import workspaceService from '../../services/workspaceService'
import {
  ReactFlow,
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  Handle,
  Position,
  MarkerType,
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import {
  Search, Filter, X, Upload, Play, Sparkles, GitBranch, Box, Package,
  FileCode, Database, Layers, MessageSquare, Monitor, ChevronDown, ChevronRight,
  PanelLeftClose, PanelLeftOpen, PanelRightClose, PanelRightOpen,
  Boxes, Trash2, Check, XCircle, Clock, FolderOpen, Plus, Save,
  Zap, Brain, Network, LayoutGrid, CheckSquare, Square, FileText, File,
  Archive, Inbox, Layout as ScreenIcon, Server, Download,
  Code2, Circle, Braces, Clipboard, ClipboardCheck, ExternalLink,
  AlertTriangle, CheckCircle, ArrowRight, Link2, Info,
  Edit3, RotateCcw, SplitSquareHorizontal, Send, Wand2, Loader2,
} from 'lucide-react'
import JSZip from 'jszip'
import { Allotment } from 'allotment'
import Editor from '@monaco-editor/react'
import 'allotment/dist/style.css'
import CodeExplorer from '../workspace/CodeExplorer'
import AIResponseModal from '../editor/AIResponseModal'
import CleanCodeModal from '../editor/CleanCodeModal'
import {
  generateJavaFromCobol,
  explainCobolCode,
  optimizeJavaCode,
  customPrompt as customPromptAI,
  cleanCode,
} from '../../services/aiService'
import { resolveSlice, applySliceEdit } from '../../utils/sliceResolver'
import './WorkspaceView.css'

// Set to true temporarily to enable verbose slice tracing in browser console
const DEBUG_SLICE = false

// ============================================================================
// OUTLINE PARSER (extracts symbols from file content for outline panel)
// ============================================================================

function parseOutlineItems(content, language) {
  if (!content) return []
  const items = []
  const lines = content.split('\n')

  if (language === 'java') {
    lines.forEach((line, idx) => {
      const lineNum = idx + 1
      const classMatch = line.match(/\b(?:public|private|protected)?\s*(?:abstract\s+|final\s+)?class\s+(\w+)/)
      if (classMatch) {
        items.push({ name: classMatch[1], type: 'class', line: lineNum })
        return
      }
      const ifaceMatch = line.match(/\b(?:public|private|protected)?\s*interface\s+(\w+)/)
      if (ifaceMatch) {
        items.push({ name: ifaceMatch[1], type: 'interface', line: lineNum })
        return
      }
      const methodMatch = line.match(/^\s*(?:public|private|protected)\s+(?:static\s+)?(?:[\w<>\[\],\s]+?)\s+(\w+)\s*\(/)
      if (methodMatch && !line.includes(' class ') && !line.includes(' interface ')) {
        items.push({ name: methodMatch[1] + '()', type: 'method', line: lineNum })
      }
    })
  } else if (language === 'json') {
    try {
      const parsed = JSON.parse(content)
      if (typeof parsed === 'object' && parsed !== null) {
        Object.keys(parsed).forEach(key => {
          const keyPattern = new RegExp(`"${key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}"\\s*:`)
          const match = content.match(keyPattern)
          const lineNum = match ? content.substring(0, match.index).split('\n').length : 1
          const valueType = Array.isArray(parsed[key]) ? 'array' : typeof parsed[key]
          items.push({ name: key, type: valueType, line: lineNum })
        })
      }
    } catch { /* invalid JSON */ }
  } else if (language === 'cobol' || language === 'plaintext') {
    lines.forEach((line, idx) => {
      const lineNum = idx + 1
      const trimmed = line.trim().toUpperCase()
      if (trimmed.match(/^\w[\w-]*\s+DIVISION\b/)) {
        items.push({ name: trimmed.replace(/\.\s*$/, ''), type: 'division', line: lineNum })
      } else if (trimmed.match(/^\w[\w-]*\s+SECTION\b/)) {
        items.push({ name: trimmed.replace(/\.\s*$/, ''), type: 'section', line: lineNum })
      } else if (trimmed.match(/^[A-Z][\w-]+\.\s*$/) && !trimmed.includes(' ')) {
        items.push({ name: trimmed.replace(/\.\s*$/, ''), type: 'paragraph', line: lineNum })
      }
    })
  }

  return items
}

// ============================================================================
// SOURCE-TARGET MAPPING (resolves COBOL source for a given Java target file)
// ============================================================================

/**
 * Given a target file path, resolve the corresponding source program.
 * Uses traceability_map.json mappings first, then falls back to name derivation.
 * Generalized for future languages - uses "source"/"target" terminology.
 */
function resolveSourceForTarget(targetPath, traceabilityData, analysisData) {
  if (!targetPath) return null
  const targetFileName = targetPath.split('/').pop()

  // Strategy 1: Traceability map lookup
  if (traceabilityData?.mappings) {
    for (const mapping of traceabilityData.mappings) {
      const mFile = mapping.target_file || mapping.java_file || ''
      if (mFile && targetPath.endsWith(mFile)) {
        const sourceName = mapping.source || mapping.cobol_name || ''
        if (sourceName) {
          const program = analysisData.programs.find(p => {
            const pName = (p.name || '').replace(/\.(cbl|cob|CBL|COB|cpy|CPY)$/i, '').toUpperCase()
            return pName === sourceName.toUpperCase()
          })
          if (program) return { path: program.path, name: program.name }
        }
      }
    }
  }

  // Strategy 2: Derive source name from target filename
  const baseName = (targetFileName || '').replace(/\.(java|cs|cpp)$/i, '')
    .replace(/Service$|Impl$|Controller$|Repository$|Entity$/i, '')
    .toUpperCase()
  if (baseName && analysisData.programs.length > 0) {
    const match = analysisData.programs.find(p => {
      const pName = (p.name || '').replace(/\.(cbl|cob|CBL|COB)$/i, '').toUpperCase()
      return pName === baseName || pName.includes(baseName) || baseName.includes(pName)
    })
    if (match) return { path: match.path, name: match.name }
  }

  return null
}

// ============================================================================
// EMPTY DEFAULTS (Real data comes from API)
// ============================================================================

const EMPTY_INVENTORY = {
  schema_version: "1.0",
  job_id: null,
  components: []
}

const EMPTY_RELATIONSHIPS = {
  relationships: []
}

const EMPTY_POSITIONS = {}

// ============================================================================
// CUSTOM NODE COMPONENT
// ============================================================================

function InventoryNode({ data, selected }) {
  const getIcon = () => {
    switch (data.componentType) {
      // COBOL/Mainframe
      case 'program': return <FileCode size={14} />
      case 'copybook': return <Layers size={14} />
      case 'jcl': return <FileText size={14} />
      case 'table': return <Database size={14} />
      case 'queue': return <Inbox size={14} />
      case 'screen': return <ScreenIcon size={14} />
      case 'vsam': return <Archive size={14} />
      case 'ims': return <Server size={14} />
      case 'sequential': return <File size={14} />
      case 'data': return <File size={14} />
      // Java
      case 'service': return <FileCode size={14} />
      case 'controller': return <Monitor size={14} />
      case 'repository': return <Database size={14} />
      case 'entity': return <Layers size={14} />
      case 'dto': return <Layers size={14} />
      case 'config': return <MessageSquare size={14} />
      case 'map': return <Monitor size={14} />
      default: return <FileCode size={14} />
    }
  }

  const getTypeLabel = () => {
    switch (data.componentType) {
      // COBOL/Mainframe
      case 'program': return 'COBOL PROGRAM'
      case 'copybook': return 'COPYBOOK'
      case 'jcl': return 'JCL'
      case 'table': return data.language === 'postgresql' ? 'PG TABLE' : 'DB2 TABLE'
      case 'queue': return 'MQ QUEUE'
      case 'screen': return 'SCREEN MAP'
      case 'vsam': return 'VSAM FILE'
      case 'ims': return 'IMS SEGMENT'
      case 'sequential': return 'SEQUENTIAL FILE'
      case 'data': return 'DATA FILE'
      // Java
      case 'service': return 'SERVICE'
      case 'controller': return 'CONTROLLER'
      case 'repository': return 'REPOSITORY'
      case 'entity': return 'ENTITY'
      case 'dto': return 'DTO'
      case 'config': return 'CONFIG'
      case 'map': return 'SCREEN'
      default: return data.componentType?.toUpperCase()
    }
  }

  return (
    <div className={`inventory-node ${data.componentType} ${selected ? 'selected' : ''} ${data.highlighted ? 'highlighted' : ''} ${data.inSlice ? 'in-slice' : ''}`}>
      <Handle type="target" position={Position.Left} />
      <Handle type="source" position={Position.Right} />

      <div className="inventory-node-header">
        {getIcon()}
        <span className="inventory-node-type">{getTypeLabel()}</span>
        {data.inSlice && <CheckSquare size={12} className="slice-indicator" />}
      </div>
      <div className="inventory-node-name">{data.label}</div>
      {data.aiSuggestion && (
        <div className="inventory-node-ai-badge" title={`AI suggests: ${data.aiSuggestion}`}>
          <Sparkles size={10} />
        </div>
      )}
    </div>
  )
}

const nodeTypes = {
  inventory: InventoryNode,
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

const createNodes = (components, positions = {}, sliceItems = [], aiNaming = {}) => {
  return components.map(comp => ({
    id: comp.id,
    type: 'inventory',
    position: positions[comp.id] || { x: 400, y: 300 },
    data: {
      label: comp.name,
      componentType: comp.type,
      inSlice: sliceItems.some(s => s.id === comp.id),
      aiSuggestion: aiNaming[comp.id]?.suggested,
      ...comp
    }
  }))
}

const getEdgeColor = (relType) => {
  // Subtle, professional colors
  switch (relType) {
    case 'CALLS': return '#8d8d8d'
    case 'INCLUDES': return '#a8a8a8'
    case 'READS': return '#7bb3ff'
    case 'UPDATES': return '#ff9999'
    case 'SENDS_TO': return '#ff9999'
    case 'USES': return '#b89ee0'
    case 'MAPS_TO': return '#66cccc'
    default: return '#a8a8a8'
  }
}

const createEdges = (relationships) => {
  return relationships.map(rel => ({
    id: rel.id,
    source: rel.from_component_id,
    target: rel.to_component_id,
    type: 'default', // Simpler, cleaner lines
    animated: false, // Remove animation for cleaner look
    // label: rel.relationship_type.toLowerCase().replace('_', ' '), // Remove labels to reduce clutter
    data: { relationshipType: rel.relationship_type },
    style: {
      stroke: getEdgeColor(rel.relationship_type),
      strokeWidth: 1.5, // Thinner lines
      strokeDasharray: (rel.relationship_type === 'INCLUDES' || rel.relationship_type === 'USES') ? '4,4' : 'none'
    },
    markerEnd: {
      type: MarkerType.ArrowClosed,
      width: 10, // Smaller arrows
      height: 10,
      color: getEdgeColor(rel.relationship_type),
    },
  }))
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function WorkspaceView({ project }) {
  // Extract project info (fallback to mock if not provided)
  const accountId = project?.account || 'mock_account'
  const applicationName = project?.name || 'GenAppSD'
  const hasRealProject = Boolean(project?.account && project?.name)

  // View state
  const [activeTab, setActiveTab] = useState('source')
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedNode, setSelectedNode] = useState(null)
  const [selectedNodes, setSelectedNodes] = useState([]) // Multi-select for slice
  const [selectedParagraph, setSelectedParagraph] = useState(null)
  const [contextMenu, setContextMenu] = useState(null)

  // Panel state
  const [leftPanelCollapsed, setLeftPanelCollapsed] = useState(false)
  const [rightPanelCollapsed, setRightPanelCollapsed] = useState(false)
  const [rightPanelTab, setRightPanelTab] = useState('properties') // 'properties' | 'slice' | 'runs' | 'ai'

  // API Loading state
  const [isLoading, setIsLoading] = useState(false)
  const [loadingMessage, setLoadingMessage] = useState('')
  const [error, setError] = useState(null)
  const [isAnalyzed, setIsAnalyzed] = useState(false)
  const [hasJavaMonolith, setHasJavaMonolith] = useState(false)
  const [javaMonolithJobId, setJavaMonolithJobId] = useState(null)
  const [detectingJava, setDetectingJava] = useState(false)

  // Real data state (populated from API, falls back to mock)
  const [realInventory, setRealInventory] = useState(null)
  const [realRelationships, setRealRelationships] = useState(null)
  const [realPositions, setRealPositions] = useState(null)
  const [realServiceBoundaries, setRealServiceBoundaries] = useState(null)

  // Use real data if available, otherwise empty defaults
  const activeInventory = realInventory || EMPTY_INVENTORY
  const activeRelationships = realRelationships || EMPTY_RELATIONSHIPS
  const activePositions = realPositions || EMPTY_POSITIONS

  // Transformation Scope state (selected components for targeted generation)
  const [currentSlice, setCurrentSlice] = useState([])
  const [sliceName, setSliceName] = useState('')

  // Workbook persistence state
  const [workbooks, setWorkbooks] = useState([])
  const [currentWorkbook, setCurrentWorkbook] = useState(null)
  const [loadingWorkbooks, setLoadingWorkbooks] = useState(false)

  // Saved runs state
  const [savedRuns, setSavedRuns] = useState([])
  const [currentRun, setCurrentRun] = useState(null)

  // AI Naming state
  const [aiNamingResults, setAiNamingResults] = useState({})
  const [approvedNames, setApprovedNames] = useState({})
  const [rejectedNames, setRejectedNames] = useState({})

  // Modal state
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [showAnalysisModal, setShowAnalysisModal] = useState(false)
  const [showServiceModal, setShowServiceModal] = useState(false)
  const [showRunFlowModal, setShowRunFlowModal] = useState(false)
  const [selectedFlow, setSelectedFlow] = useState(null)

  // Upload state
  const [uploadFile, setUploadFile] = useState(null)
  const [selectedIngestJobId, setSelectedIngestJobId] = useState(null)  // 2026-01-27: Changed from uploadSourceHash to selectedIngestJobId

  // Refactor panel state (AI service boundary suggestions - populated from API)
  const [showRefactorPanel, setShowRefactorPanel] = useState(false)
  const [refactorSuggestions, setRefactorSuggestions] = useState([])

  // Service generation output (displayed on Target tab with Monaco editor)
  const [serviceGenJobId, setServiceGenJobId] = useState(null)
  const [targetResults, setTargetResults] = useState(null)       // API results (stats, services, validation)
  const [targetFileMap, setTargetFileMap] = useState({})          // {path: content} from ZIP
  const [targetSelectedFile, setTargetSelectedFile] = useState(null) // {path, content, language}
  const [targetLoading, setTargetLoading] = useState(false)
  const [targetTreeExpanded, setTargetTreeExpanded] = useState({
    source_programs: true, source_copybooks: false,
    services: true, entities: false, reports: false, indexes: false
  })
  const [targetTreeFilter, setTargetTreeFilter] = useState('')

  // IDE Tab System (Target tab)
  const [openTabs, setOpenTabs] = useState([])              // [{path, label, language, source}]
  const [activeTabPath, setActiveTabPath] = useState(null)   // path of active tab

  // Outline Panel (Target tab)
  const [targetOutlineItems, setTargetOutlineItems] = useState([])  // [{name, type, line}]

  // Right Context Panel tabs (Target tab)
  const [targetContextTab, setTargetContextTab] = useState('outline') // 'outline' | 'issues' | 'traceability'
  const [traceabilitySearch, setTraceabilitySearch] = useState('')
  const [copiedToClipboard, setCopiedToClipboard] = useState(false)

  // COBOL File Content Cache (for source files opened in Target tab)
  const [cobolFileCache, setCobolFileCache] = useState({})   // {path: content}

  // Procedure Model (for CodeExplorer paragraphs + COBOL outline)
  const [procedureModelData, setProcedureModelData] = useState(null)

  // Monaco editor ref for outline click-to-jump
  const targetEditorRef = useRef(null)

  // Split editor (Source vs Target side-by-side)
  const [splitMode, setSplitMode] = useState(false)
  const [sourceContent, setSourceContent] = useState('')
  const [sourceLanguage, setSourceLanguage] = useState('cobol')
  const [sourcePath, setSourcePath] = useState(null)
  const sourceEditorRef = useRef(null)
  const [activeEditor, setActiveEditor] = useState('target') // 'source' | 'target'

  // Dirty state (editable target files)
  const [editedFiles, setEditedFiles] = useState({})        // {path: editedContent}
  const [dirtyPaths, setDirtyPaths] = useState(new Set())   // paths with unsaved changes

  // AI command palette
  const [aiModal, setAiModal] = useState({ isOpen: false, mode: 'explain', title: '', selectedCode: '', sectionName: '' })
  const [aiResponse, setAiResponse] = useState(null)
  const [aiLoading, setAiLoading] = useState(false)
  const [aiNotification, setAiNotification] = useState(null) // { message: string } | null

  // AI Clean Code
  const [cleanCodeModal, setCleanCodeModal] = useState(null) // { isOpen, original, cleaned, filename, language }
  const [cleanCodeLoading, setCleanCodeLoading] = useState(false)
  const handleCleanCodeRef = useRef(null) // stable ref so Monaco action always calls latest handler

  // Slice workbench
  const [sliceState, setSliceState] = useState(null)
  const [sliceViewMode, setSliceViewMode] = useState('full') // 'full' | 'slice'
  const [sliceEditContent, setSliceEditContent] = useState(null) // edited slice text or null
  const [sliceBanner, setSliceBanner] = useState(null) // { type: 'info'|'warning', message }

  const reactFlowInstance = useRef(null)

  // Debug: Log component mount
  console.log('[WorkspaceView] Component rendering/mounted', { hasRealProject, accountId, applicationName })

  // Restore analysis state from localStorage on mount/project change
  // Mirrors the workbook restore pattern (line ~644) - localStorage is the source of truth
  // for ephemeral UI state that must survive page reloads
  useEffect(() => {
    if (!hasRealProject) return

    let restoredJavaMonolith = false

    try {
      const saved = localStorage.getItem(`workspace_analysisState_${accountId}_${applicationName}`)
      if (saved) {
        const state = JSON.parse(saved)
        console.debug(' [Workspace] Restoring analysis state from localStorage:', state)
        if (state.selectedIngestJobId) {
          setSelectedIngestJobId(state.selectedIngestJobId)
          console.debug(` [Workspace] Restored selectedIngestJobId: ${state.selectedIngestJobId.slice(0, 12)}...`)
        }
        if (state.isAnalyzed) setIsAnalyzed(true)
        if (state.hasJavaMonolith) {
          setHasJavaMonolith(true)
          restoredJavaMonolith = true
          if (state.javaMonolithJobId) setJavaMonolithJobId(state.javaMonolithJobId)
        }
        if (state.serviceGenJobId) {
          setServiceGenJobId(state.serviceGenJobId)
        }
      }
    } catch (e) {
      console.warn('[Workspace] Failed to restore analysis state:', e)
    }

    // If Java monolith was NOT in localStorage, detect from API
    // Handles: pre-existing projects, Workflow-generated Java, wiped localStorage
    if (!restoredJavaMonolith) {
      console.log(' [Workspace] hasJavaMonolith not in localStorage, checking API...')
      setDetectingJava(true)
      workspaceService.detectExistingJavaMonolith(accountId, applicationName)
        .then(({ found, jobId }) => {
          if (found) {
            console.log(' [Workspace] Java monolith DETECTED via API, job:', jobId)
            setHasJavaMonolith(true)
            setJavaMonolithJobId(jobId)
            // Persist so we don't need to re-detect next time
            const lsKey = `workspace_analysisState_${accountId}_${applicationName}`
            try {
              const prev = JSON.parse(localStorage.getItem(lsKey) || '{}')
              localStorage.setItem(lsKey, JSON.stringify({ ...prev, hasJavaMonolith: true, javaMonolithJobId: jobId }))
            } catch { /* non-fatal */ }
          } else {
            console.log(' [Workspace] No existing Java monolith found via API')
          }
        })
        .catch(err => console.warn(' [Workspace] Java detection failed (non-fatal):', err))
        .finally(() => setDetectingJava(false))
    }
  }, [hasRealProject, accountId, applicationName])

  // Fetch procedure_model.json when javaMonolithJobId becomes available
  // This provides paragraph data for CodeExplorer (Source tab) and COBOL outline (Target tab)
  useEffect(() => {
    if (!javaMonolithJobId || procedureModelData) return
    console.log(' [Workspace] Fetching procedure model for job:', javaMonolithJobId)
    workspaceService.fetchProcedureModel(javaMonolithJobId)
      .then(model => {
        if (model) {
          console.log(' [Workspace] Procedure model loaded:', model.paragraphs?.length || 0, 'paragraphs')
          setProcedureModelData(model)
        }
      })
      .catch(err => console.warn(' [Workspace] Procedure model fetch failed (non-fatal):', err))
  }, [javaMonolithJobId, procedureModelData])

  // ============================================================================
  // REAL API CALL HANDLERS
  // ============================================================================

  // Fetch API results + ZIP when Target tab activated with a saved jobId
  useEffect(() => {
    if (activeTab !== 'target' || !serviceGenJobId || targetLoading) return
    if (targetResults && Object.keys(targetFileMap).length > 0) return // Already loaded

    console.log(' [Workspace] Target tab active, loading results + ZIP for:', serviceGenJobId)
    setTargetLoading(true)

    Promise.all([
      workspaceService.fetchServiceGenResults(serviceGenJobId).catch(err => {
        console.warn(' [Workspace] Results API failed (non-fatal):', err)
        return null
      }),
      workspaceService.downloadServiceZip(serviceGenJobId)
    ])
      .then(async ([results, blob]) => {
        if (results) {
          console.log(' [Workspace] API results loaded:', results.service_count, 'services')
          setTargetResults(results)
        }

        const zip = await JSZip.loadAsync(blob)
        const textExts = ['.java', '.json', '.xml', '.md', '.yml', '.yaml', '.properties', '.txt']
        const fileMap = {}
        for (const [path, entry] of Object.entries(zip.files)) {
          if (entry.dir) continue
          const ext = path.substring(path.lastIndexOf('.')).toLowerCase()
          const isText = textExts.some(e => ext === e)
          fileMap[path] = isText ? await entry.async('string') : '[Binary file]'
        }
        setTargetFileMap(fileMap)
        console.log(' [Workspace] ZIP extracted:', Object.keys(fileMap).length, 'files')

        // Auto-select first service file and add to tab bar
        const firstService = results?.services?.[0]?.filename
        const firstServicePath = firstService
          ? Object.keys(fileMap).find(p => p.endsWith(firstService))
          : Object.keys(fileMap).find(p => p.endsWith('.java'))
        if (firstServicePath) {
          const lang = firstServicePath.endsWith('.json') ? 'json' : 'java'
          const label = firstServicePath.split('/').pop()
          setTargetSelectedFile({ path: firstServicePath, content: fileMap[firstServicePath], language: lang })
          setOpenTabs([{ path: firstServicePath, label, language: lang, source: 'target' }])
          setActiveTabPath(firstServicePath)
        }
      })
      .catch(err => {
        console.warn(' [Workspace] Failed to load target data:', err)
        setError('Failed to load generated files: ' + err.message)
      })
      .finally(() => setTargetLoading(false))
  }, [activeTab, serviceGenJobId]) // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Run full analysis pipeline (Code Analysis + Dependency Mapper + Data Analysis)
   * 2026-01-27: Updated to use selectedIngestJobId instead of source_hash
   */
  const handleRunAnalysis = useCallback(async () => {
    if (!hasRealProject) {
      console.log('[Workspace] No real project - cannot run analysis')
      setError('Select a project first to run analysis')
      return
    }

    // 2026-01-27: Require ingest job ID to be selected
    if (!selectedIngestJobId) {
      setError('Please upload files first or select an existing upload')
      return
    }

    setIsLoading(true)
    setError(null)
    setLoadingMessage('Starting analysis...')

    try {
      const result = await workspaceService.runFullAnalysis(
        accountId,
        applicationName,
        selectedIngestJobId,  // 2026-01-27: Pass selected ingest job ID
        (progress) => {
          setLoadingMessage(progress.message || `Running ${progress.step}...`)
        }
      )

      if (result.success) {
        setRealInventory(result.inventory)
        setRealRelationships(result.relationships)
        setRealPositions(result.positions)
        setRealServiceBoundaries(result.serviceBoundaries)

        // If we got service boundary suggestions, update refactorSuggestions
        if (result.serviceBoundaries?.length > 0) {
          setRefactorSuggestions(result.serviceBoundaries)
        }

        setIsAnalyzed(true)
        // Persist for page reload restoration (merge to preserve selectedIngestJobId + hasJavaMonolith)
        const lsKey_a = `workspace_analysisState_${accountId}_${applicationName}`
        try { const prev = JSON.parse(localStorage.getItem(lsKey_a) || '{}'); localStorage.setItem(lsKey_a, JSON.stringify({ ...prev, isAnalyzed: true, timestamp: Date.now() })) }
        catch { localStorage.setItem(lsKey_a, JSON.stringify({ isAnalyzed: true, hasJavaMonolith, javaMonolithJobId, selectedIngestJobId, timestamp: Date.now() })) }
        setLoadingMessage('Analysis complete!')
      } else {
        setError(result.error || 'Analysis failed')
        setLoadingMessage('')
      }
    } catch (err) {
      console.error('[Workspace] Analysis error:', err)
      setError(err.message || 'Analysis failed')
      setLoadingMessage('')
    } finally {
      setIsLoading(false)
    }
  }, [accountId, applicationName, hasRealProject, selectedIngestJobId])

  /**
   * Generate Java Monolith - runs Code Analysis with Java generation enabled
   */
  const handleGenerateJavaMonolith = useCallback(async () => {
    if (!hasRealProject) {
      setError('Select a project first to generate Java')
      return
    }

    if (!selectedIngestJobId) {
      setError('Please upload files first or select an existing upload')
      return
    }

    setIsLoading(true)
    setError(null)
    setLoadingMessage('Generating Java monolith...')

    try {
      const result = await workspaceService.runJavaMonolithGeneration(
        accountId,
        applicationName,
        selectedIngestJobId,
        (progress) => {
          setLoadingMessage(progress.message || 'Generating Java...')
        }
      )

      console.debug(' [Workspace] Generate Java result:', { success: result.success, jobId: result.jobId, javaMonolithUri: result.javaMonolithUri })

      if (result.success) {
        console.debug(' [Workspace] Setting hasJavaMonolith = true')
        setHasJavaMonolith(true)
        setJavaMonolithJobId(result.jobId)
        // Persist for page reload restoration (merge to preserve selectedIngestJobId)
        const lsKey_j = `workspace_analysisState_${accountId}_${applicationName}`
        try { const prev = JSON.parse(localStorage.getItem(lsKey_j) || '{}'); localStorage.setItem(lsKey_j, JSON.stringify({ ...prev, isAnalyzed: true, hasJavaMonolith: true, javaMonolithJobId: result.jobId, timestamp: Date.now() })) }
        catch { localStorage.setItem(lsKey_j, JSON.stringify({ isAnalyzed: true, hasJavaMonolith: true, javaMonolithJobId: result.jobId, selectedIngestJobId, timestamp: Date.now() })) }
        setLoadingMessage('Java monolith generated!')

        // Auto-clear success message after 3 seconds
        setTimeout(() => setLoadingMessage(''), 3000)
      } else {
        setError(result.error || 'Java generation failed')
        setLoadingMessage('')
      }
    } catch (err) {
      console.error('[Workspace] Java monolith generation error:', err)
      setError(err.message || 'Java generation failed')
      setLoadingMessage('')
    } finally {
      setIsLoading(false)
    }
  }, [accountId, applicationName, hasRealProject, selectedIngestJobId])

  /**
   * Run AI Naming flow
   */
  const handleRunAINaming = useCallback(async () => {
    if (!hasRealProject) {
      console.log('[Workspace] No real project - cannot run AI Naming')
      setError('Select a project first to run AI Naming')
      return
    }

    setIsLoading(true)
    setError(null)
    setLoadingMessage('Running AI Naming...')

    try {
      const result = await workspaceService.runAINaming(
        accountId,
        applicationName,
        (progress) => {
          setLoadingMessage(progress.message || 'Processing AI naming...')
        }
      )

      if (result.success) {
        setAiNamingResults(result.suggestions)
        setLoadingMessage('AI Naming complete!')
        setRightPanelTab('ai')
        setRightPanelCollapsed(false)
      } else {
        setError(result.error || 'AI Naming failed')
      }
    } catch (err) {
      console.error('[Workspace] AI Naming error:', err)
      setError(err.message || 'AI Naming failed')
    } finally {
      setIsLoading(false)
      setLoadingMessage('')
    }
  }, [accountId, applicationName, hasRealProject])

  /**
   * Run Service Generation with current scope
   * Orchestrates end-to-end: Java prep (if needed) → Service Generation
   */
  const handleGenerateService = useCallback(async () => {
    if (currentSlice.length === 0) {
      setError('No components in scope. Add components before generating.')
      return
    }

    if (!hasRealProject) {
      setError('Select a project first to generate services')
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      // Step 1: Ensure Java monolith exists (generate if needed)
      if (!hasJavaMonolith) {
        if (!selectedIngestJobId) {
          setError('Please upload files first. Java generation requires an uploaded source.')
          setIsLoading(false)
          return
        }

        setLoadingMessage('Preparing Java source (step 1 of 2)...')

        const javaResult = await workspaceService.runJavaMonolithGeneration(
          accountId,
          applicationName,
          selectedIngestJobId,
          (progress) => {
            setLoadingMessage(progress.message || 'Generating Java...')
          }
        )

        if (!javaResult.success) {
          setError(javaResult.error || 'Java generation failed')
          setIsLoading(false)
          setLoadingMessage('')
          return
        }

        // Update state + persist
        setHasJavaMonolith(true)
        setJavaMonolithJobId(javaResult.jobId)
        const lsKey = `workspace_analysisState_${accountId}_${applicationName}`
        try {
          const prev = JSON.parse(localStorage.getItem(lsKey) || '{}')
          localStorage.setItem(lsKey, JSON.stringify({ ...prev, hasJavaMonolith: true, javaMonolithJobId: javaResult.jobId, timestamp: Date.now() }))
        } catch { /* non-fatal */ }
      }

      // Step 2: Run Service Generation
      setLoadingMessage(hasJavaMonolith ? 'Generating service...' : 'Generating service (step 2 of 2)...')

      const result = await workspaceService.runServiceGeneration(
        accountId,
        applicationName,
        sliceName || 'GeneratedService',
        currentSlice,
        (progress) => {
          setLoadingMessage(progress.message || 'Generating service...')
        }
      )

      if (result.success) {
        setServiceGenJobId(result.jobId)
        setTargetResults(null)       // Reset so useEffect re-fetches
        setTargetFileMap({})         // Reset so useEffect re-fetches ZIP
        setTargetSelectedFile(null)
        setOpenTabs([])              // Clear all open tabs
        setActiveTabPath(null)
        setTargetOutlineItems([])
        setCobolFileCache({})
        setTargetContextTab('outline')
        setTraceabilitySearch('')
        setSplitMode(false)
        setSourceContent('')
        setSourcePath(null)
        setEditedFiles({})
        setDirtyPaths(new Set())
        setAiModal({ isOpen: false, mode: 'explain', title: '', selectedCode: '', sectionName: '' })
        setAiResponse(null)
        setSliceState(null)
        setSliceViewMode('full')
        setSliceEditContent(null)
        // Persist jobId to localStorage
        const lsKey_sg = `workspace_analysisState_${accountId}_${applicationName}`
        try {
          const prev_sg = JSON.parse(localStorage.getItem(lsKey_sg) || '{}')
          localStorage.setItem(lsKey_sg, JSON.stringify({ ...prev_sg, serviceGenJobId: result.jobId }))
        } catch { /* non-fatal */ }
        setLoadingMessage('Service generated!')
        setShowServiceModal(false)
        setActiveTab('target')
      } else {
        setError(result.error || 'Service generation failed')
      }
    } catch (err) {
      console.error('[Workspace] Service generation error:', err)

      if (err.message && err.message.includes('AWS_CREDENTIALS_REQUIRED')) {
        const parts = err.message.split('|')
        const userMessage = parts.length > 1 ? parts[1] : err.message
        setError(userMessage)
        setShowServiceModal(false)
      } else {
        setError(err.message || 'Service generation failed')
      }
    } finally {
      setIsLoading(false)
      setLoadingMessage('')
    }
  }, [accountId, applicationName, hasRealProject, currentSlice, sliceName, hasJavaMonolith, selectedIngestJobId])

  /**
   * Download generated service ZIP from S3
   */
  const handleDownloadService = useCallback(async () => {
    if (!serviceGenJobId) return
    try {
      setIsLoading(true)
      setLoadingMessage('Preparing download...')
      const blob = await workspaceService.downloadServiceZip(serviceGenJobId)
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${serviceGenJobId}_services.zip`
      a.click()
      URL.revokeObjectURL(url)
    } catch (err) {
      console.error('[Workspace] Download failed:', err)
      setError('Download failed: ' + err.message)
    } finally {
      setIsLoading(false)
      setLoadingMessage('')
    }
  }, [serviceGenJobId])

  /**
   * Upload COBOL ZIP file
   */
  const handleUpload = useCallback(async () => {
    console.log('[WorkspaceView] handleUpload called', { uploadFile, hasRealProject, accountId, applicationName })

    if (!uploadFile) {
      console.log('[WorkspaceView] No upload file selected')
      setError('Please select a ZIP file to upload')
      return
    }

    if (!hasRealProject) {
      console.log('[WorkspaceView] No real project')
      setError('Select a project first to upload files')
      return
    }

    // Get user_id from localStorage (set during login as JSON)
    const userStr = localStorage.getItem('modernizeit_user')
    let userId = null
    if (userStr) {
      try {
        const userData = JSON.parse(userStr)
        userId = userData.user_id
        console.log('[WorkspaceView] Got user ID:', userId)
      } catch (e) {
        console.error('[Workspace] Failed to parse user data:', e)
      }
    }
    if (!userId) {
      console.log('[WorkspaceView] No user ID')
      setError('User not logged in. Please log in to upload files.')
      return
    }

    console.log('[WorkspaceView] Starting upload...')
    setIsLoading(true)
    setError(null)
    setLoadingMessage('Uploading files...')

    try {
      const result = await workspaceService.uploadCobolFiles(
        accountId,
        applicationName,
        userId,
        uploadFile,
        (progress) => {
          console.log('[WorkspaceView] Upload progress:', progress)
          setLoadingMessage(progress.message || 'Uploading...')
        }
      )

      console.log('[WorkspaceView] Upload result:', result)

      if (result.success) {
        // 2026-01-27: Set ingest job ID instead of source hash
        setSelectedIngestJobId(result.ingestJobId)
        // Persist ingest job ID for page reload restoration (merge with existing state)
        const lsKey = `workspace_analysisState_${accountId}_${applicationName}`
        try {
          const existing = JSON.parse(localStorage.getItem(lsKey) || '{}')
          localStorage.setItem(lsKey, JSON.stringify({ ...existing, selectedIngestJobId: result.ingestJobId, timestamp: Date.now() }))
        } catch { localStorage.setItem(lsKey, JSON.stringify({ selectedIngestJobId: result.ingestJobId, timestamp: Date.now() })) }
        setLoadingMessage('Upload complete!')
        console.log(' UPLOAD SUCCESS! Job ID:', result.ingestJobId)
        setShowUploadModal(false)
        setUploadFile(null)
      } else {
        setError(result.error || 'Upload failed')
      }
    } catch (err) {
      console.error('[Workspace] Upload error:', err)
      setError(err.message || 'Upload failed')
    } finally {
      setIsLoading(false)
      setLoadingMessage('')
    }
  }, [accountId, applicationName, hasRealProject, uploadFile])

  // ============================================================================
  // DATA DERIVATION (uses active data - real or mock)
  // ============================================================================

  // Analysis data for CodeExplorer (populated from real API response)
  // ✅ FIX: Filter each type separately to avoid duplicates
  const analysisData = useMemo(() => {
    const components = activeInventory.components || []

    // Filter by type to avoid duplicates
    // Programs includes: program, jcl, data, sequential, vsam, ims, queue, screen
    const programs = components.filter(c =>
      !c.type || c.type === 'program' || c.type === 'jcl' || c.type === 'data' ||
      c.type === 'sequential' || c.type === 'vsam' || c.type === 'ims' ||
      c.type === 'queue' || c.type === 'screen'
    )
    const copybooks = components.filter(c => c.type === 'copybook')
    const tables = components.filter(c => c.type === 'table')

    console.log(' [WorkspaceView] Building analysisData for CodeExplorer:', {
      totalComponents: components.length,
      programs: programs.length,
      copybooks: copybooks.length,
      tables: tables.length,
      sum: programs.length + copybooks.length + tables.length,
    })

    // Build paragraphs map from procedure_model (enables CodeExplorer paragraph sub-tree)
    const paragraphs = {}
    if (procedureModelData?.paragraphs) {
      const programName = procedureModelData.source_file ||
        programs.find(p => !p.type || p.type === 'program')?.name || 'UNKNOWN'
      paragraphs[programName] = procedureModelData.paragraphs.map(p => ({
        name: p.name,
        division: 'PROCEDURE',
        line_start: p.start_line,
        line_end: p.end_line,
      }))
    }

    return {
      programs,
      copybooks,
      tables,
      paragraphs,
    }
  }, [activeInventory, procedureModelData])

  // ReactFlow nodes and edges (use active data)
  const initialNodes = useMemo(() =>
    createNodes(activeInventory.components, activePositions, currentSlice, aiNamingResults),
    [activeInventory, activePositions, currentSlice, aiNamingResults]
  )
  const initialEdges = useMemo(() => createEdges(activeRelationships.relationships), [activeRelationships])
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes)
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges)

  // Update nodes/edges when data changes (includes slice changes via currentSlice dep)
  useEffect(() => {
    const newNodes = createNodes(activeInventory.components, activePositions, currentSlice, aiNamingResults)
    const newEdges = createEdges(activeRelationships.relationships)

    // ✅ Debug logging for edge verification
    console.log(' [WorkspaceView] Updating graph state:', {
      nodes: newNodes.length,
      edges: newEdges.length,
      relationships: activeRelationships.relationships?.length || 0,
      sampleEdges: newEdges.slice(0, 5).map(e => ({
        id: e.id,
        source: e.source,
        target: e.target,
        type: e.data?.relationshipType
      }))
    })

    setNodes(newNodes)
    setEdges(newEdges)
  }, [activeInventory, activeRelationships, activePositions, currentSlice, aiNamingResults, setNodes, setEdges])

  // Load workbooks from API when project changes
  useEffect(() => {
    if (!hasRealProject || !project?.accountId) {
      setWorkbooks([])
      setCurrentWorkbook(null)
      return
    }

    const loadWorkbooks = async () => {
      setLoadingWorkbooks(true)
      try {
        const wbs = await workspaceService.listWorkbooks(project.accountId)
        setWorkbooks(wbs || [])

        // If there's an active workbook, try to restore it
        const storedWbId = localStorage.getItem(`workspace_currentWorkbook_${project.accountId}`)
        if (storedWbId && wbs) {
          const wb = wbs.find(w => w.workbook_id === storedWbId)
          if (wb) {
            // Load full workbook with items
            const fullWb = await workspaceService.getWorkbook(project.accountId, wb.workbook_id)
            setCurrentWorkbook(fullWb.workbook)
            // Populate currentSlice from workbook items
            const items = fullWb.items.map(item => ({
              id: item.paragraph_id,
              name: item.paragraph_name,
              type: 'paragraph',
              programName: item.program_name,
              lineStart: item.line_start,
              itemId: item.item_id,  // Track database item ID
              status: item.status
            }))
            setCurrentSlice(items)
          }
        }
      } catch (error) {
        console.error('[WorkspaceView] Failed to load workbooks (non-fatal):', error)
        // Don't break the page - workbooks are optional
        setWorkbooks([])
        setCurrentWorkbook(null)
      } finally {
        setLoadingWorkbooks(false)
      }
    }

    // Use setTimeout to avoid blocking the render
    const timer = setTimeout(loadWorkbooks, 100)
    return () => clearTimeout(timer)
  }, [hasRealProject, project?.accountId])

  // Filtered nodes based on search
  const filteredNodes = useMemo(() => {
    const q = (searchQuery || '').trim().toLowerCase()
    if (!q) return nodes
    return nodes.filter(n => {
      const label = String(n?.data?.label ?? '').toLowerCase()
      const type = String(n?.data?.type ?? '').toLowerCase()
      return label.includes(q) || type.includes(q)
    })
  }, [nodes, searchQuery])

  const filteredEdges = useMemo(() => {
    const visible = new Set(filteredNodes.map(n => n.id))
    const filtered = edges.filter(e => visible.has(e.source) && visible.has(e.target))

    // ✅ Debug logging for edge filtering
    console.log(' [WorkspaceView] Filtering edges:', {
      totalEdges: edges.length,
      visibleNodes: visible.size,
      filteredEdges: filtered.length,
      droppedEdges: edges.length - filtered.length,
    })

    return filtered
  }, [edges, filteredNodes])

  // ============================================================================
  // SLICE FUNCTIONS
  // ============================================================================

  const addToSlice = useCallback((node) => {
    const item = {
      id: node.id,
      name: node.data?.name || node.data?.label,
      type: node.data?.componentType || node.data?.type
    }
    setCurrentSlice(prev => {
      if (prev.some(s => s.id === item.id)) return prev
      return [...prev, item]
    })
  }, [])

  const removeFromSlice = useCallback(async (itemId) => {
    // Update local state immediately (optimistic update)
    setCurrentSlice(prev => prev.filter(s => s.id !== itemId))

    // Try to remove from database (optional - don't fail if API is down)
    try {
      const item = currentSlice.find(s => s.id === itemId)
      if (item?.itemId) {
        await workspaceService.removeWorkbookItem(item.itemId)
      }
    } catch (error) {
      console.warn('[WorkspaceView] Failed to remove item from database (local state updated):', error)
      // Don't revert - local state is already updated
    }
  }, [currentSlice])

  const clearSlice = useCallback(() => {
    setCurrentSlice([])
    setSliceName('')
  }, [])

  const addSelectedToSlice = useCallback(() => {
    if (selectedNodes.length > 0) {
      selectedNodes.forEach(node => addToSlice(node))
    } else if (selectedNode) {
      addToSlice(selectedNode)
    }
  }, [selectedNodes, selectedNode, addToSlice])

  // Add dependencies to slice
  const addDependenciesToSlice = useCallback(() => {
    const currentIds = new Set(currentSlice.map(s => s.id))
    const newItems = []

    currentSlice.forEach(item => {
      // Find outgoing relationships (use active data - real or mock)
      activeRelationships.relationships.forEach(rel => {
        if (rel.from_component_id === item.id && !currentIds.has(rel.to_component_id)) {
          const comp = activeInventory.components.find(c => c.id === rel.to_component_id)
          if (comp) {
            newItems.push({ id: comp.id, name: comp.name, type: comp.type })
            currentIds.add(comp.id)
          }
        }
      })
    })

    if (newItems.length > 0) {
      setCurrentSlice(prev => [...prev, ...newItems])
    }
  }, [currentSlice, activeRelationships, activeInventory])

  // ============================================================================
  // AI NAMING FUNCTIONS
  // ============================================================================

  const approveAiName = useCallback((componentId) => {
    setApprovedNames(prev => ({ ...prev, [componentId]: true }))
    setRejectedNames(prev => {
      const next = { ...prev }
      delete next[componentId]
      return next
    })
  }, [])

  const rejectAiName = useCallback((componentId) => {
    setRejectedNames(prev => ({ ...prev, [componentId]: true }))
    setApprovedNames(prev => {
      const next = { ...prev }
      delete next[componentId]
      return next
    })
  }, [])

  // ============================================================================
  // REFACTOR FUNCTIONS
  // ============================================================================

  const addSuggestionToScope = useCallback((suggestion) => {
    const currentIds = new Set(currentSlice.map(s => s.id))
    const newItems = suggestion.components
      .filter(comp => !currentIds.has(comp.id))
      .map(comp => ({ id: comp.id, name: comp.name, type: comp.type }))

    if (newItems.length > 0) {
      setCurrentSlice(prev => [...prev, ...newItems])
      setSliceName(suggestion.serviceName)
    }
    setShowRefactorPanel(false)
    setRightPanelTab('slice')
  }, [currentSlice])

  // ============================================================================
  // NODE INTERACTION
  // ============================================================================

  const onNodeClick = useCallback((event, node) => {
    // Handle multi-select with shift key
    if (event?.shiftKey) {
      setSelectedNodes(prev => {
        if (prev.some(n => n.id === node.id)) {
          return prev.filter(n => n.id !== node.id)
        }
        return [...prev, node]
      })
    } else {
      setSelectedNode(node)
      setSelectedNodes([])
    }

    // Highlight connected nodes (use active data - real or mock)
    const connectedNodeIds = new Set([node.id])
    const connectedEdgeIds = new Set()

    activeRelationships.relationships.forEach(rel => {
      if (rel.from_component_id === node.id) {
        connectedNodeIds.add(rel.to_component_id)
        connectedEdgeIds.add(rel.id)
      }
      if (rel.to_component_id === node.id) {
        connectedNodeIds.add(rel.from_component_id)
        connectedEdgeIds.add(rel.id)
      }
    })

    setNodes(nds => nds.map(n => ({
      ...n,
      data: { ...n.data, highlighted: connectedNodeIds.has(n.id) },
      style: { ...n.style, opacity: connectedNodeIds.has(n.id) ? 1 : 0.35 }
    })))

    setEdges(eds => eds.map(e => {
      const isConnected = connectedEdgeIds.has(e.id)
      const relType = e.data?.relationshipType || 'default'
      return {
        ...e,
        style: {
          ...e.style,
          strokeWidth: isConnected ? 2 : 1.5,
          stroke: isConnected ? getEdgeColor(relType) : '#d0d0d0',
          opacity: isConnected ? 1 : 0.3
        },
        animated: false // Keep animations off
      }
    }))
  }, [setNodes, setEdges, activeRelationships])

  const onPaneClick = useCallback(() => {
    setSelectedNode(null)
    setSelectedNodes([])
    setContextMenu(null)

    setNodes(nds => nds.map(n => ({
      ...n,
      data: { ...n.data, highlighted: false },
      style: { ...n.style, opacity: 1 }
    })))

    setEdges(eds => eds.map(e => {
      const relType = e.data?.relationshipType || 'default'
      return {
        ...e,
        style: {
          ...e.style,
          strokeWidth: 1.5,
          stroke: getEdgeColor(relType),
          opacity: 1
        },
        animated: false
      }
    }))
  }, [setNodes, setEdges])

  // ✅ ESC key handler to clear selection
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        // Clear selection and context menu on ESC
        onPaneClick()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [onPaneClick])

  // ============================================================================
  // CONTEXT MENU
  // ============================================================================

  const onNodeContextMenu = useCallback((event, node) => {
    event.preventDefault()

    const isInSlice = currentSlice.some(s => s.id === node.id)
    const hasAiSuggestion = aiNamingResults[node.id]
    const isApproved = approvedNames[node.id]
    const isRejected = rejectedNames[node.id]

    setContextMenu({
      x: event.clientX,
      y: event.clientY,
      node: node,
      items: [
        { key: 'open-editor', label: 'Open in Code Editor', icon: <FileCode size={14} />, enabled: true },
        { key: 'view-properties', label: 'View Properties', icon: <Search size={14} />, enabled: true },
        { key: 'show-dependencies', label: 'Show Dependencies', icon: <GitBranch size={14} />, enabled: true },
        { key: 'focus-node', label: 'Focus on This Node', icon: <Monitor size={14} />, enabled: true },
        { key: 'divider-1' },
        {
          key: isInSlice ? 'remove-from-slice' : 'add-to-slice',
          label: isInSlice ? 'Remove from Scope' : 'Include in Scope',
          icon: isInSlice ? <Square size={14} /> : <CheckSquare size={14} />,
          enabled: true,
          highlight: !isInSlice
        },
        { key: 'add-with-deps', label: 'Include with Dependencies', icon: <Network size={14} />, enabled: !isInSlice },
        { key: 'divider-2' },
        {
          key: 'approve-naming',
          label: 'Approve AI Name',
          icon: <Check size={14} />,
          enabled: hasAiSuggestion && !isApproved,
          success: true
        },
        {
          key: 'reject-naming',
          label: 'Reject AI Name',
          icon: <XCircle size={14} />,
          enabled: hasAiSuggestion && !isRejected,
          danger: true
        },
      ]
    })
  }, [currentSlice, aiNamingResults, approvedNames, rejectedNames])

  const handleMenuAction = useCallback(async (action, node) => {
    setContextMenu(null)

    switch (action) {
      case 'open-editor':
        console.log(' [WorkspaceView] Open in Code Editor:', node.data.name)

        // Fetch file content if not already loaded
        if (!node.data.source_code && node.data.path) {
          console.log(' [WorkspaceView] Fetching source code for:', node.data.path)

          try {
            const fileData = await workspaceService.fetchFileContent(
              accountId,
              applicationName,
              node.data.path
            )

            // Update node data with source code
            node.data.source_code = fileData.content
            node.data.line_count = fileData.line_count
            node.data.language = fileData.language

            console.log(' [WorkspaceView] Source code loaded:', {
              path: fileData.path,
              size: fileData.size,
              lines: fileData.line_count,
              language: fileData.language
            })
          } catch (error) {
            console.error(' [WorkspaceView] Failed to load source code:', error)
            node.data.source_code = `// Error loading source code\n// ${error.message}`
          }
        }

        // Switch to Workbook tab and select this node
        setSelectedNode(node)
        setActiveTab('workbook')
        break
      case 'view-properties':
        setSelectedNode(node)
        setRightPanelTab('properties')
        setRightPanelCollapsed(false)
        break
      case 'show-dependencies':
        onNodeClick(null, node)
        break
      case 'focus-node':
        if (reactFlowInstance.current) {
          const { x, y } = node.position
          reactFlowInstance.current.setCenter(x + 60, y + 30, { zoom: 1.5, duration: 800 })
        }
        break
      case 'add-to-slice':
        addToSlice(node)
        setRightPanelTab('slice')
        setRightPanelCollapsed(false)
        break
      case 'remove-from-slice':
        removeFromSlice(node.id)
        break
      case 'add-with-deps':
        addToSlice(node)
        // Find and add dependencies (use active data - real or mock)
        activeRelationships.relationships.forEach(rel => {
          if (rel.from_component_id === node.id) {
            const comp = activeInventory.components.find(c => c.id === rel.to_component_id)
            if (comp) addToSlice({ id: comp.id, data: comp })
          }
        })
        setRightPanelTab('slice')  // Internal state name
        setRightPanelCollapsed(false)
        break
      case 'approve-naming':
        approveAiName(node.id)
        break
      case 'reject-naming':
        rejectAiName(node.id)
        break
      default:
        console.log('Unknown action:', action)
    }
  }, [onNodeClick, addToSlice, removeFromSlice, approveAiName, rejectAiName, activeRelationships, activeInventory])

  // Handle explorer node selection
  const handleExplorerNodeSelect = useCallback((node) => {
    const flowNode = nodes.find(n =>
      n.data.name === node.name || n.data.label === node.name || n.id === node.id
    )

    if (flowNode) {
      onNodeClick(null, flowNode)
      if (reactFlowInstance.current) {
        const { x, y } = flowNode.position
        reactFlowInstance.current.setCenter(x + 60, y + 30, { zoom: 1.2, duration: 800 })
      }
    }
  }, [nodes, onNodeClick])

  const onInit = useCallback((instance) => {
    reactFlowInstance.current = instance
  }, [])

  const onConnect = useCallback(() => {}, [])

  // ============================================================================
  // RENDER: ACTION TOOLBAR
  // ============================================================================

  const renderActionToolbar = () => {
    console.debug(' [Workspace] Toolbar gating:', { isLoading, isAnalyzed, hasJavaMonolith, hasRealProject, selectedIngestJobId, accountId, applicationName, sliceCount: currentSlice.length, javaMonolithJobId })
    return (
    <div className="ws-action-toolbar">
      {/* Loading indicator */}
      {isLoading && (
        <div className="ws-toolbar-loading">
          <div className="ws-loading-spinner" />
          <span>{loadingMessage || 'Loading...'}</span>
        </div>
      )}

      {/* Error indicator */}
      {error && !isLoading && (
        <div className="ws-toolbar-error">
          <XCircle size={14} />
          <span>{error}</span>
          <button onClick={() => setError(null)} title="Dismiss">
            <X size={12} />
          </button>
        </div>
      )}

      {/* Project indicator */}
      {hasRealProject && (
        <div className="ws-toolbar-project">
          <span className="ws-project-label">Project:</span>
          <span className="ws-project-name">{applicationName}</span>
          {selectedIngestJobId && (
            <span style={{ color: '#4CAF50', marginLeft: '8px', fontWeight: 'bold' }} title={`Upload complete! Job ID: ${selectedIngestJobId}`}>
              ✓ Uploaded
            </span>
          )}
          {isAnalyzed && <Check size={12} className="ws-analyzed-check" title="Analyzed" />}
        </div>
      )}

      <div className="ws-toolbar-divider" />

      {/* Step 1: Upload */}
      <div className="ws-toolbar-group">
        <button
          className="ws-toolbar-btn primary"
          onClick={() => setShowUploadModal(true)}
          disabled={isLoading}
          title="Upload COBOL Files"
        >
          <Upload size={14} />
          Upload
        </button>
      </div>

      <div className="ws-toolbar-divider" />

      {/* Step 2: Analyze (Combined: Code + Dependencies + Data) - Calls real API */}
      <div className="ws-toolbar-group">
        <button
          className={`ws-toolbar-btn ${isAnalyzed ? 'analyzed' : ''}`}
          onClick={handleRunAnalysis}
          disabled={isLoading || !selectedIngestJobId}
          title={!selectedIngestJobId
            ? "Upload files first to enable analysis"
            : hasRealProject
              ? "Run Full Analysis (Code + Dependencies + Data)"
              : "No project selected - showing mock data"
          }
        >
          <Play size={14} />
          {isLoading && loadingMessage?.includes('nalysis') ? 'Analyzing...' : 'Analyze'}
        </button>
      </div>

      <div className="ws-toolbar-divider" />

      {/* Generate Java - handled automatically by Generate Service modal */}
      <div className="ws-toolbar-group">
        <button
          className={`ws-toolbar-btn ${hasJavaMonolith ? 'analyzed' : ''}`}
          disabled={true}
          title="Java generation is handled automatically when you Generate Service from the Transformation Scope panel"
        >
          <Box size={14} />
          Generate Java
        </button>
        {hasJavaMonolith && <Check size={12} className="ws-analyzed-check" title="Java Generated" />}
      </div>

      <div className="ws-toolbar-divider" />

      {/* Step 3: AI Naming (Optional but recommended) - Calls real API */}
      <div className="ws-toolbar-group">
        <button
          className="ws-toolbar-btn accent"
          onClick={handleRunAINaming}
          disabled={isLoading || (!isAnalyzed && hasRealProject)}
          title={!isAnalyzed && hasRealProject
            ? "Run Analyze first"
            : "Run AI Naming - Get semantic names for programs and methods"
          }
        >
          <Sparkles size={14} />
          {isLoading && loadingMessage?.includes('Naming') ? 'Naming...' : 'AI Naming'}
        </button>
      </div>

      <div className="ws-toolbar-divider" />

      {/* Step 4: Refactor (AI suggests service boundaries) */}
      <div className="ws-toolbar-group">
        <button
          className="ws-toolbar-btn"
          onClick={() => setShowRefactorPanel(true)}
          disabled={isLoading || (!isAnalyzed && hasRealProject)}
          title="AI suggests service boundaries - helps you decide what to group"
        >
          <Brain size={14} />
          Refactor
        </button>
      </div>

      <div className="ws-toolbar-divider" />

      {/* Generate Service - use the Transformation Scope panel button instead */}
      <div className="ws-toolbar-group">
        <button
          className="ws-toolbar-btn success"
          disabled={true}
          title="Use the Generate Service button in the Transformation Scope panel (right panel → select components first)"
        >
          <Package size={14} />
          Generate Service
        </button>
      </div>
    </div>
  )}

  // ============================================================================
  // RENDER: SLICE PANEL
  // ============================================================================

  const renderSlicePanel = () => (
    <div className="ws-slice-panel">
      <div className="ws-slice-header">
        <Boxes size={16} />
        <span>Transformation Scope</span>
        <span className="ws-slice-count">{currentSlice.length}</span>
      </div>

      {currentSlice.length === 0 ? (
        <div className="ws-slice-empty">
          <Boxes size={32} />
          <p>No components selected</p>
          <span>Right-click nodes to include them in scope</span>
        </div>
      ) : (
        <>
          <div className="ws-slice-name-input">
            <label>Service Name</label>
            <input
              type="text"
              value={sliceName}
              onChange={(e) => setSliceName(e.target.value)}
              placeholder="Enter service name..."
            />
          </div>

          <div className="ws-slice-items">
            {currentSlice.map(item => (
              <div key={item.id} className={`ws-slice-item ${item.type}`}>
                <div className="ws-slice-item-info">
                  {item.type === 'program' && <FileCode size={14} />}
                  {item.type === 'copybook' && <Layers size={14} />}
                  {item.type === 'table' && <Database size={14} />}
                  <span className="ws-slice-item-name">{item.name}</span>
                  <span className="ws-slice-item-type">{item.type}</span>
                </div>
                <button
                  className="ws-slice-item-remove"
                  onClick={() => removeFromSlice(item.id)}
                  title="Remove from scope"
                >
                  <X size={14} />
                </button>
              </div>
            ))}
          </div>

          <div className="ws-slice-suggestions">
            <button
              className="ws-slice-suggest-btn"
              onClick={addDependenciesToSlice}
            >
              <Plus size={14} />
              Add Dependencies
            </button>
          </div>

          <div className="ws-slice-actions">
            <button
              className="ws-slice-action-btn secondary"
              onClick={clearSlice}
            >
              <Trash2 size={14} />
              Clear
            </button>
            <button
              className="ws-slice-action-btn primary"
              onClick={() => setShowServiceModal(true)}
              disabled={isLoading || !isAnalyzed}
              title={!isAnalyzed
                ? "Run Analyze first before generating services"
                : "Generate Spring Boot microservices from selected scope"
              }
            >
              <Package size={14} />
              Generate Service
            </button>
          </div>
        </>
      )}
    </div>
  )

  // ============================================================================
  // RENDER: SAVED RUNS PANEL
  // ============================================================================

  const renderSavedRunsPanel = () => (
    <div className="ws-runs-panel">
      <div className="ws-runs-header">
        <Clock size={16} />
        <span>Saved Runs</span>
      </div>

      {savedRuns.length === 0 ? (
        <div className="ws-runs-empty">
          <Clock size={32} />
          <p>No saved runs</p>
          <span>Run analysis and save your results to create a run</span>
        </div>
      ) : (
        <div className="ws-runs-list">
          {savedRuns.map(run => (
            <div
              key={run.id}
              className={`ws-run-item ${run.isCurrent ? 'current' : ''}`}
              onClick={() => setCurrentRun(run)}
            >
              <div className="ws-run-item-header">
                <span className="ws-run-name">{run.name}</span>
                {run.isCurrent && <span className="ws-run-current-badge">Current</span>}
              </div>
              <div className="ws-run-item-meta">
                <span>{new Date(run.timestamp).toLocaleDateString()}</span>
                <span>{run.stats?.programs || 0} programs</span>
              </div>
              <div className="ws-run-flows">
                <span className={`ws-flow-status ${run.flows?.codeAnalysis || 'pending'}`} title="Code Analysis">CA</span>
                <span className={`ws-flow-status ${run.flows?.dependencyMapper || 'pending'}`} title="Dependency Mapper">DM</span>
                <span className={`ws-flow-status ${run.flows?.dataAnalysis || 'pending'}`} title="Data Analysis">DA</span>
                <span className={`ws-flow-status ${run.flows?.aiNaming || 'pending'}`} title="AI Naming">AI</span>
              </div>
              {run.slices?.length > 0 && (
                <div className="ws-run-slices">
                  {run.slices.map(s => (
                    <span key={s} className="ws-run-slice-tag">{s}</span>
                  ))}
                </div>
              )}
              <div className="ws-run-actions">
                <button className="ws-run-action-btn" title="Load this run">
                  <FolderOpen size={12} />
                  Load
                </button>
                <button className="ws-run-action-btn" title="Compare with current">
                  <GitBranch size={12} />
                  Compare
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <button className="ws-new-run-btn" disabled={!isAnalyzed}>
        <Plus size={14} />
        Save Current as New Run
      </button>
    </div>
  )

  // ============================================================================
  // RENDER: AI NAMING PANEL
  // ============================================================================

  const renderAiNamingPanel = () => {
    const suggestions = Object.entries(aiNamingResults)

    return (
      <div className="ws-ai-panel">
        <div className="ws-ai-header">
          <Sparkles size={16} />
          <span>AI Naming Suggestions</span>
          <span className="ws-ai-count">{suggestions.length}</span>
        </div>

        <div className="ws-ai-scope">
          <label>Scope</label>
          <select defaultValue="scope">
            <option value="all">All Programs</option>
            <option value="scope">Current Scope ({currentSlice.length})</option>
            <option value="selected">Selected Only</option>
          </select>
          <button className="ws-ai-run-btn">
            <Play size={12} />
            Run AI Naming
          </button>
        </div>

        <div className="ws-ai-suggestions">
          {suggestions.map(([compId, suggestion]) => {
            const isApproved = approvedNames[compId]
            const isRejected = rejectedNames[compId]

            return (
              <div
                key={compId}
                className={`ws-ai-suggestion ${isApproved ? 'approved' : ''} ${isRejected ? 'rejected' : ''}`}
              >
                <div className="ws-ai-suggestion-header">
                  <span className="ws-ai-original">{suggestion.original}</span>
                  <span className="ws-ai-arrow">→</span>
                  <span className="ws-ai-suggested">{suggestion.suggested}</span>
                </div>
                <div className="ws-ai-confidence">
                  <div
                    className="ws-ai-confidence-bar"
                    style={{ width: `${suggestion.confidence * 100}%` }}
                  />
                  <span>{Math.round(suggestion.confidence * 100)}%</span>
                </div>
                <div className="ws-ai-reasoning">{suggestion.reasoning}</div>
                <div className="ws-ai-actions">
                  <button
                    className={`ws-ai-action approve ${isApproved ? 'active' : ''}`}
                    onClick={() => approveAiName(compId)}
                    disabled={isApproved}
                  >
                    <Check size={14} />
                    {isApproved ? 'Approved' : 'Approve'}
                  </button>
                  <button
                    className={`ws-ai-action reject ${isRejected ? 'active' : ''}`}
                    onClick={() => rejectAiName(compId)}
                    disabled={isRejected}
                  >
                    <XCircle size={14} />
                    {isRejected ? 'Rejected' : 'Reject'}
                  </button>
                </div>
              </div>
            )
          })}
        </div>

        <div className="ws-ai-batch-actions">
          <button className="ws-ai-batch-btn">
            <Check size={14} />
            Approve All
          </button>
          <button className="ws-ai-batch-btn secondary">
            <XCircle size={14} />
            Reject All
          </button>
        </div>
      </div>
    )
  }

  // ============================================================================
  // RENDER: REFACTOR PANEL (AI Service Boundary Suggestions)
  // ============================================================================

  const renderRefactorPanel = () => (
    <div className="ws-refactor-modal-overlay" onClick={() => setShowRefactorPanel(false)}>
      <div className="ws-refactor-panel" onClick={e => e.stopPropagation()}>
        <div className="ws-refactor-header">
          <Brain size={20} />
          <h2>AI Service Recommendations</h2>
          <button className="ws-refactor-close" onClick={() => setShowRefactorPanel(false)}>
            <X size={18} />
          </button>
        </div>

        <p className="ws-refactor-intro">
          Based on code analysis, here's how AI suggests grouping components into services.
          Click "Add to Scope" to use a suggestion, or build your own scope manually.
        </p>

        <div className="ws-refactor-suggestions">
          {refactorSuggestions.length === 0 ? (
            <div className="ws-refactor-empty">
              <Brain size={32} />
              <p>No service boundary suggestions</p>
              <span>Run Analyze first, then AI will suggest how to group components into services</span>
            </div>
          ) : (
            refactorSuggestions.map(suggestion => (
              <div key={suggestion.id} className={`ws-refactor-suggestion ${(suggestion.confidence || 'medium').toLowerCase()}`}>
                <div className="ws-refactor-suggestion-header">
                  <div className="ws-refactor-service-name">
                    <Package size={16} />
                    <span>{suggestion.serviceName}</span>
                  </div>
                  <span className={`ws-refactor-confidence ${(suggestion.confidence || 'medium').toLowerCase()}`}>
                    {suggestion.confidence || 'MEDIUM'} confidence
                  </span>
                </div>

                <div className="ws-refactor-components">
                  {(suggestion.components || []).map(comp => (
                    <div key={comp.id} className="ws-refactor-component">
                      <div className="ws-refactor-component-icon">
                        {comp.type === 'program' && <FileCode size={14} />}
                        {comp.type === 'table' && <Database size={14} />}
                        {comp.type === 'copybook' && <Layers size={14} />}
                      </div>
                      <span className="ws-refactor-component-name">{comp.name}</span>
                      <span className="ws-refactor-component-reason">{comp.reason}</span>
                    </div>
                  ))}
                </div>

                <div className="ws-refactor-reasoning">
                  <Sparkles size={14} />
                  <span>{suggestion.reasoning || 'AI analysis pending'}</span>
                </div>

                <div className="ws-refactor-actions">
                  <button
                    className="ws-refactor-action-btn primary"
                    onClick={() => addSuggestionToScope(suggestion)}
                  >
                    <CheckSquare size={14} />
                    Add to Scope
                  </button>
                  <button className="ws-refactor-action-btn secondary">
                    Ignore
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        <div className="ws-refactor-footer">
          <p>Don't see what you need? Close this panel and manually select components from the graph.</p>
        </div>
      </div>
    </div>
  )

  // ============================================================================
  // RENDER: PROPERTIES PANEL
  // ============================================================================

  const renderPropertiesPanel = () => {
    if (!selectedNode) {
      return (
        <div className="ws-prop-empty">
          <Monitor size={32} />
          <p>Select a node to view properties</p>
        </div>
      )
    }

    const nodeData = selectedNode.data || {}
    const aiSuggestion = aiNamingResults[selectedNode.id]

    // Get relationships (use active data - real or mock)
    const incoming = activeRelationships.relationships
      .filter(r => r.to_component_id === selectedNode.id)
      .map(r => {
        const source = activeInventory.components.find(c => c.id === r.from_component_id)
        return { name: source?.name, type: r.relationship_type }
      })

    const outgoing = activeRelationships.relationships
      .filter(r => r.from_component_id === selectedNode.id)
      .map(r => {
        const target = activeInventory.components.find(c => c.id === r.to_component_id)
        return { name: target?.name, type: r.relationship_type }
      })

    return (
      <div className="ws-properties-content">
        <div className="ws-prop-type">{nodeData.componentType || nodeData.type}</div>
        <div className="ws-prop-name">{nodeData.name || nodeData.label}</div>

        {aiSuggestion && (
          <div className="ws-prop-ai-suggestion">
            <div className="ws-prop-ai-header">
              <Sparkles size={12} />
              <span>AI Suggested Name</span>
            </div>
            <div className="ws-prop-ai-name">{aiSuggestion.suggested}</div>
            <div className="ws-prop-ai-confidence">
              Confidence: {Math.round(aiSuggestion.confidence * 100)}%
            </div>
          </div>
        )}

        {nodeData.path && (
          <div className="ws-prop-meta">
            <span className="ws-meta-label">Path</span>
            <span className="ws-meta-value">{nodeData.path}</span>
          </div>
        )}

        {nodeData.metadata?.lines && (
          <div className="ws-prop-meta">
            <span className="ws-meta-label">Lines</span>
            <span className="ws-meta-value">{nodeData.metadata.lines}</span>
          </div>
        )}

        {nodeData.tags?.length > 0 && (
          <div className="ws-prop-tags">
            {nodeData.tags.map(tag => (
              <span key={tag} className="ws-tag">{tag}</span>
            ))}
          </div>
        )}

        <div className="ws-prop-section">
          <h4>Incoming ({incoming.length})</h4>
          <div className="ws-references">
            {incoming.length === 0 ? (
              <div className="ws-ref-empty">None</div>
            ) : (
              incoming.map((r, i) => (
                <div key={i} className="ws-ref-item">
                  <span className="ws-ref-name">{r.name}</span>
                  <span className="ws-ref-type">{r.type}</span>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="ws-prop-section">
          <h4>Outgoing ({outgoing.length})</h4>
          <div className="ws-references">
            {outgoing.length === 0 ? (
              <div className="ws-ref-empty">None</div>
            ) : (
              outgoing.map((r, i) => (
                <div key={i} className="ws-ref-item">
                  <span className="ws-ref-name">{r.name}</span>
                  <span className="ws-ref-type">{r.type}</span>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="ws-prop-actions">
          <button
            className="ws-prop-action-btn"
            onClick={() => addToSlice(selectedNode)}
            disabled={currentSlice.some(s => s.id === selectedNode.id)}
          >
            {currentSlice.some(s => s.id === selectedNode.id) ? <CheckSquare size={14} /> : <Square size={14} />}
            {currentSlice.some(s => s.id === selectedNode.id) ? 'Included in Scope' : 'Include in Scope'}
          </button>
        </div>
      </div>
    )
  }

  // ============================================================================
  // RENDER: UPLOAD MODAL
  // ============================================================================

  const renderUploadModal = () => {
    if (!showUploadModal) return null

    return (
      <div className="ws-modal-overlay" onClick={() => setShowUploadModal(false)}>
        <div className="ws-modal" onClick={e => e.stopPropagation()}>
          <div className="ws-modal-header">
            <Upload size={20} />
            <h2>Upload COBOL Files</h2>
            <button className="ws-modal-close" onClick={() => setShowUploadModal(false)}>
              <X size={18} />
            </button>
          </div>

          <div className="ws-modal-body">
            {!hasRealProject ? (
              <div className="ws-modal-warning">
                <XCircle size={20} />
                <p>Please select a project first from the landing page.</p>
              </div>
            ) : (
              <>
                <div className="ws-modal-section">
                  <label>Project</label>
                  <div className="ws-modal-project-info">
                    <span className="ws-modal-project-name">{applicationName}</span>
                    <span className="ws-modal-project-account">({accountId})</span>
                  </div>
                </div>

                <div className="ws-modal-section">
                  <label>Select ZIP File</label>
                  <div className="ws-upload-dropzone">
                    <input
                      type="file"
                      accept=".zip"
                      onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                      className="ws-upload-input"
                      id="upload-file-input"
                    />
                    <label htmlFor="upload-file-input" className="ws-upload-label">
                      <Upload size={24} />
                      {uploadFile ? (
                        <span className="ws-upload-filename">{uploadFile.name}</span>
                      ) : (
                        <span>Click to select or drag ZIP file here</span>
                      )}
                    </label>
                  </div>
                  {uploadFile && (
                    <div className="ws-upload-file-info">
                      <span>Size: {(uploadFile.size / 1024).toFixed(1)} KB</span>
                    </div>
                  )}
                </div>

                <div className="ws-modal-section">
                  <label>What happens next:</label>
                  <ul className="ws-modal-output-list">
                    <li><Check size={14} /> ZIP is uploaded to S3 storage</li>
                    <li><Check size={14} /> Files are extracted and cataloged</li>
                    <li><Check size={14} /> COBOL type mappings are generated</li>
                    <li><Check size={14} /> Ready for analysis</li>
                  </ul>
                </div>
              </>
            )}
          </div>

          <div className="ws-modal-footer">
            <button
              className="ws-modal-btn secondary"
              onClick={() => {
                setShowUploadModal(false)
                setUploadFile(null)
              }}
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              className="ws-modal-btn primary"
              onClick={handleUpload}
              disabled={isLoading || !uploadFile || !hasRealProject}
            >
              <Upload size={14} />
              {isLoading ? 'Uploading...' : 'Upload'}
            </button>
          </div>
        </div>
      </div>
    )
  }

  // ============================================================================
  // RENDER: SERVICE CREATION MODAL
  // ============================================================================

  const renderServiceModal = () => {
    if (!showServiceModal) return null

    return (
      <div className="ws-modal-overlay" onClick={() => setShowServiceModal(false)}>
        <div className="ws-modal" onClick={e => e.stopPropagation()}>
          <div className="ws-modal-header">
            <Package size={20} />
            <h2>Create Service from Selection</h2>
            <button className="ws-modal-close" onClick={() => setShowServiceModal(false)}>
              <X size={18} />
            </button>
          </div>

          <div className="ws-modal-body">
            <div className="ws-modal-section">
              <label>Service Name</label>
              <input
                type="text"
                value={sliceName}
                onChange={(e) => setSliceName(e.target.value)}
                placeholder="Enter service name..."
                className="ws-modal-input"
              />
              {aiNamingResults[currentSlice[0]?.id] && (
                <div className="ws-modal-ai-suggest">
                  <Sparkles size={12} />
                  <span>AI suggests: </span>
                  <button onClick={() => setSliceName(aiNamingResults[currentSlice[0]?.id]?.suggested)}>
                    {aiNamingResults[currentSlice[0]?.id]?.suggested}
                  </button>
                </div>
              )}
            </div>

            <div className="ws-modal-section">
              <label>Selected Items ({currentSlice.length})</label>
              <div className="ws-modal-items">
                {currentSlice.map(item => (
                  <div key={item.id} className="ws-modal-item">
                    {item.type === 'program' && <FileCode size={14} />}
                    {item.type === 'copybook' && <Layers size={14} />}
                    {item.type === 'table' && <Database size={14} />}
                    <span>{item.name}</span>
                    <span className="ws-modal-item-type">{item.type}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="ws-modal-section">
              <label>This will generate:</label>
              <ul className="ws-modal-output-list">
                <li><FileCode size={14} /> {sliceName || 'Service'}.java (service class)</li>
                <li><Database size={14} /> CustomerRepository.java (if tables included)</li>
                <li><Layers size={14} /> Customer.java (entity from table)</li>
              </ul>
            </div>
          </div>

          <div className="ws-modal-footer">
            <button
              className="ws-modal-btn secondary"
              onClick={() => setShowServiceModal(false)}
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              className="ws-modal-btn primary"
              onClick={handleGenerateService}
              disabled={isLoading || currentSlice.length === 0}
            >
              <Zap size={14} />
              {isLoading ? 'Generating...' : 'Generate Service'}
            </button>
          </div>
        </div>
      </div>
    )
  }

  // ============================================================================
  // RENDER: RUN FLOW MODAL
  // ============================================================================

  const renderRunFlowModal = () => {
    if (!showRunFlowModal) return null

    const flowNames = {
      analyze: 'Full Analysis',
      aiNaming: 'AI Naming',
    }

    const flowDescriptions = {
      analyze: 'Run full analysis pipeline: Code Analysis (parse programs, paragraphs, copybooks) + Dependency Mapper (call graphs, transitive dependencies) + Data Analysis (ERD, tables, data lineage). This is the foundation for all other operations.',
      aiNaming: 'Use AI to suggest semantic names for programs and methods. Review and approve/reject suggestions before generation.',
    }

    return (
      <div className="ws-modal-overlay" onClick={() => setShowRunFlowModal(false)}>
        <div className="ws-modal" onClick={e => e.stopPropagation()}>
          <div className="ws-modal-header">
            <Play size={20} />
            <h2>Run {flowNames[selectedFlow]}</h2>
            <button className="ws-modal-close" onClick={() => setShowRunFlowModal(false)}>
              <X size={18} />
            </button>
          </div>

          <div className="ws-modal-body">
            <p className="ws-modal-description">{flowDescriptions[selectedFlow]}</p>

            {selectedFlow === 'aiNaming' && (
              <div className="ws-modal-section">
                <label>Scope</label>
                <select className="ws-modal-select">
                  <option value="all">All Programs ({activeInventory.components.filter(c => c.type === 'program').length})</option>
                  <option value="scope">Current Scope ({currentSlice.length})</option>
                </select>
              </div>
            )}

            <div className="ws-modal-section">
              <label>Estimated Output</label>
              <ul className="ws-modal-output-list">
                {selectedFlow === 'analyze' && (
                  <>
                    <li><strong>Code Analysis:</strong> Programs, paragraphs, copybooks</li>
                    <li><strong>Dependencies:</strong> Call graphs, transitive relationships</li>
                    <li><strong>Data Analysis:</strong> ERD, tables, data lineage</li>
                    <li>Graph and tree menu fully populated</li>
                  </>
                )}
                {selectedFlow === 'aiNaming' && (
                  <>
                    <li>naming_suggestions.json</li>
                    <li>Semantic names for programs and methods</li>
                    <li>Review in AI Naming tab</li>
                  </>
                )}
              </ul>
            </div>
          </div>

          <div className="ws-modal-footer">
            <button
              className="ws-modal-btn secondary"
              onClick={() => setShowRunFlowModal(false)}
            >
              Cancel
            </button>
            <button
              className="ws-modal-btn primary"
              onClick={() => {
                alert(`Running ${flowNames[selectedFlow]}...`)
                setShowRunFlowModal(false)
              }}
            >
              <Play size={14} />
              Run {flowNames[selectedFlow]}
            </button>
          </div>
        </div>
      </div>
    )
  }

  // ============================================================================
  // RENDER: TARGET TAB (3-Pane IDE: Explorer | Editor with Tabs | Outline)
  // ============================================================================

  // --- Language detection helper ---
  const detectLanguage = useCallback((path) => {
    if (path.endsWith('.json')) return 'json'
    if (path.endsWith('.xml')) return 'xml'
    if (path.endsWith('.yaml') || path.endsWith('.yml')) return 'yaml'
    if (path.endsWith('.md')) return 'markdown'
    if (path.endsWith('.cbl') || path.endsWith('.cpy') || path.endsWith('.cob')) return 'cobol'
    return 'java'
  }, [])

  // --- Dirty state: get effective content (edited or original) ---
  const getEffectiveContent = useCallback((path) => {
    return editedFiles[path] !== undefined ? editedFiles[path] : (targetFileMap[path] || '')
  }, [editedFiles, targetFileMap])

  // --- Tab management ---
  const openFileInTab = useCallback(async (path, source = 'target') => {
    // If tab already open, just switch to it
    const existingTab = openTabs.find(t => t.path === path)
    if (existingTab) {
      setActiveTabPath(path)
      const content = source === 'cobol'
        ? (cobolFileCache[path] || '')
        : getEffectiveContent(path)
      setTargetSelectedFile({ path, content, language: existingTab.language })
      return
    }

    let content = ''
    let language = detectLanguage(path)

    if (source === 'target') {
      content = getEffectiveContent(path)
    } else if (source === 'cobol') {
      language = 'cobol'
      if (cobolFileCache[path]) {
        content = cobolFileCache[path]
      } else if (selectedIngestJobId) {
        try {
          content = await workspaceService.fetchCobolFileForWorkspace(selectedIngestJobId, path)
          setCobolFileCache(prev => ({ ...prev, [path]: content }))
        } catch (err) {
          console.error(' [Workspace] Failed to load COBOL file:', err)
          content = `// Error loading file: ${err.message}`
        }
      } else {
        content = '// No ingest job available. Upload COBOL files first.'
      }
    }

    const label = path.split('/').pop()
    setOpenTabs(prev => [...prev, { path, label, language, source }])
    setActiveTabPath(path)
    setTargetSelectedFile({ path, content, language })
  }, [openTabs, targetFileMap, cobolFileCache, selectedIngestJobId, detectLanguage, getEffectiveContent])

  const closeTab = useCallback((path, e) => {
    if (e) e.stopPropagation()
    setOpenTabs(prev => {
      const newTabs = prev.filter(t => t.path !== path)
      if (activeTabPath === path) {
        const closedIdx = prev.findIndex(t => t.path === path)
        const nextTab = newTabs[Math.min(closedIdx, newTabs.length - 1)]
        if (nextTab) {
          setActiveTabPath(nextTab.path)
          const content = nextTab.source === 'cobol'
            ? (cobolFileCache[nextTab.path] || '')
            : getEffectiveContent(nextTab.path)
          setTargetSelectedFile({ path: nextTab.path, content, language: nextTab.language })
        } else {
          setActiveTabPath(null)
          setTargetSelectedFile(null)
        }
      }
      return newTabs
    })
  }, [activeTabPath, cobolFileCache, targetFileMap, getEffectiveContent])

  const switchTab = useCallback((path) => {
    setActiveTabPath(path)
    const tab = openTabs.find(t => t.path === path)
    if (tab) {
      const content = tab.source === 'cobol'
        ? (cobolFileCache[path] || '')
        : getEffectiveContent(path)
      setTargetSelectedFile({ path, content, language: tab.language })
    }
  }, [openTabs, cobolFileCache, targetFileMap, getEffectiveContent])

  const toggleTargetSection = useCallback((section) => {
    setTargetTreeExpanded(prev => ({ ...prev, [section]: !prev[section] }))
  }, [])

  // --- Parse Issues (validation_report.json) from targetFileMap ---
  const issuesData = useMemo(() => {
    const reportPath = Object.keys(targetFileMap).find(p => p.endsWith('validation_report.json'))
    if (!reportPath) return null
    try {
      const parsed = JSON.parse(targetFileMap[reportPath])
      const checks = parsed.checks || parsed.validation_checks || []
      const errors = checks.filter(c => c.severity === 'error' || c.status === 'FAIL')
      const warnings = checks.filter(c => c.severity === 'warning' || c.status === 'WARN')
      const passed = checks.filter(c => c.severity === 'info' || c.status === 'PASS' || c.passed)
      return {
        reportPath,
        passed: parsed.passed ?? parsed.validation_passed ?? (errors.length === 0),
        totalChecks: checks.length,
        errorCount: errors.length,
        warningCount: warnings.length,
        passedCount: passed.length,
        checks,
        errors,
        warnings,
      }
    } catch { return null }
  }, [targetFileMap])

  // --- Parse Traceability (traceability_map.json) from targetFileMap ---
  const traceabilityData = useMemo(() => {
    const mapPath = Object.keys(targetFileMap).find(p => p.endsWith('traceability_map.json'))
    if (!mapPath) return null
    try {
      const parsed = JSON.parse(targetFileMap[mapPath])
      const mappings = parsed.mappings || parsed.methods || (Array.isArray(parsed) ? parsed : [])
      return { mapPath, mappings }
    } catch { return null }
  }, [targetFileMap])

  // --- Update outline when active file changes (follows active editor in split mode) ---
  useEffect(() => {
    if (splitMode && activeEditor === 'source' && sourceContent) {
      setTargetOutlineItems(parseOutlineItems(sourceContent, sourceLanguage))
    } else if (targetSelectedFile) {
      setTargetOutlineItems(parseOutlineItems(targetSelectedFile.content, targetSelectedFile.language))
    } else {
      setTargetOutlineItems([])
    }
  }, [targetSelectedFile, splitMode, activeEditor, sourceContent, sourceLanguage])

  // --- Auto-resolve source file when split mode + target file changes ---
  useEffect(() => {
    if (!splitMode || !targetSelectedFile) return
    const activeTab = openTabs.find(t => t.path === activeTabPath)
    if (activeTab?.source === 'cobol') return // Already viewing source

    const resolved = resolveSourceForTarget(targetSelectedFile.path, traceabilityData, analysisData)
    console.log('[Split] resolveSourceForTarget:', targetSelectedFile.path, '→', resolved)
    if (!resolved) {
      setSourceContent('')
      setSourcePath(null)
      return
    }

    setSourcePath(resolved.path)
    setSourceLanguage('cobol')

    // Load content from cache or fetch
    if (cobolFileCache[resolved.path]) {
      setSourceContent(cobolFileCache[resolved.path])
    } else if (selectedIngestJobId) {
      console.log('[Split] Fetching COBOL file:', resolved.path, 'via ingest job:', selectedIngestJobId)
      workspaceService.fetchCobolFileForWorkspace(selectedIngestJobId, resolved.path)
        .then(content => {
          setCobolFileCache(prev => ({ ...prev, [resolved.path]: content }))
          setSourceContent(content)
        })
        .catch(err => {
          console.error('Failed to load source for split view:', err)
          setSourceContent(`// Error loading source: ${err.message}`)
        })
    } else {
      setSourceContent('// No ingest job available. Upload files to view source.')
    }
  }, [splitMode, targetSelectedFile, activeTabPath, openTabs, traceabilityData, analysisData, cobolFileCache, selectedIngestJobId])

  // --- Copy file content to clipboard ---
  const handleCopyFileContent = useCallback(() => {
    if (!targetSelectedFile?.content) return
    navigator.clipboard.writeText(targetSelectedFile.content).then(() => {
      setCopiedToClipboard(true)
      setTimeout(() => setCopiedToClipboard(false), 2000)
    })
  }, [targetSelectedFile])

  // --- Open raw file in new browser tab ---
  const handleOpenRaw = useCallback(() => {
    if (!targetSelectedFile?.content) return
    const blob = new Blob([targetSelectedFile.content], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    window.open(url, '_blank')
    setTimeout(() => URL.revokeObjectURL(url), 5000)
  }, [targetSelectedFile])

  // --- Dirty state: handle target editor changes ---
  const handleTargetEditorChange = useCallback((newValue) => {
    if (!activeTabPath || !targetSelectedFile) return
    const path = targetSelectedFile.path
    const originalContent = targetFileMap[path] || ''
    if (newValue === originalContent) {
      setEditedFiles(prev => { const next = { ...prev }; delete next[path]; return next })
      setDirtyPaths(prev => { const next = new Set(prev); next.delete(path); return next })
    } else {
      setEditedFiles(prev => ({ ...prev, [path]: newValue }))
      setDirtyPaths(prev => new Set(prev).add(path))
    }
    setTargetSelectedFile(prev => prev ? { ...prev, content: newValue } : prev)
  }, [activeTabPath, targetSelectedFile, targetFileMap])

  // --- Dirty state: save edits to localStorage ---
  const handleSaveEdits = useCallback(() => {
    if (!serviceGenJobId || Object.keys(editedFiles).length === 0) return
    try {
      localStorage.setItem(`ws_edits_${serviceGenJobId}`, JSON.stringify(editedFiles))
    } catch (e) { console.error('Failed to save edits:', e) }
  }, [serviceGenJobId, editedFiles])

  // --- Dirty state: revert a single file ---
  const handleRevertFile = useCallback((path) => {
    setEditedFiles(prev => { const next = { ...prev }; delete next[path]; return next })
    setDirtyPaths(prev => { const next = new Set(prev); next.delete(path); return next })
    if (targetSelectedFile?.path === path) {
      setTargetSelectedFile(prev => prev ? { ...prev, content: targetFileMap[path] || '' } : prev)
    }
  }, [targetFileMap, targetSelectedFile])

  // --- Dirty state: restore from localStorage on jobId change ---
  useEffect(() => {
    if (!serviceGenJobId) return
    try {
      const saved = localStorage.getItem(`ws_edits_${serviceGenJobId}`)
      if (saved) {
        const parsed = JSON.parse(saved)
        setEditedFiles(parsed)
        setDirtyPaths(new Set(Object.keys(parsed)))
      }
    } catch { /* non-fatal */ }
  }, [serviceGenJobId])

  // --- Dirty state: auto-save after 2s debounce ---
  useEffect(() => {
    if (!serviceGenJobId || Object.keys(editedFiles).length === 0) return
    const timer = setTimeout(() => handleSaveEdits(), 2000)
    return () => clearTimeout(timer)
  }, [editedFiles, serviceGenJobId, handleSaveEdits])

  // --- Export ZIP with edits ---
  const handleExportWithEdits = useCallback(async () => {
    if (!serviceGenJobId) return
    setIsLoading(true)
    try {
      const zip = new JSZip()
      Object.keys(targetFileMap).forEach(path => {
        zip.file(path, editedFiles[path] !== undefined ? editedFiles[path] : targetFileMap[path])
      })
      const blob = await zip.generateAsync({ type: 'blob' })
      const a = document.createElement('a')
      a.href = URL.createObjectURL(blob)
      a.download = `${serviceGenJobId}_services_edited.zip`
      a.click()
      URL.revokeObjectURL(a.href)
    } catch (err) { console.error('Export failed:', err) }
    finally { setIsLoading(false) }
  }, [serviceGenJobId, targetFileMap, editedFiles])

  // --- AI: handle editor AI action ---
  const handleEditorAiAction = useCallback((action, editor) => {
    if (!editor) return
    const selection = editor.getSelection()
    const model = editor.getModel()
    const selectedText = (selection && !selection.isEmpty())
      ? model.getValueInRange(selection)
      : model.getLineContent(selection?.startLineNumber || 1)
    const sectionName = targetSelectedFile?.path?.split('/').pop() || 'Selected Code'
    const titles = { generate: 'Generate Java Code', explain: 'AI Code Explanation', optimize: 'Refactoring Suggestions', custom: 'Custom AI Analysis' }
    setAiModal({ isOpen: true, mode: action, title: titles[action] || 'AI Analysis', selectedCode: selectedText, sectionName })
    if (action === 'custom') { setAiLoading(false); setAiResponse(null); return }
    setAiLoading(true)
    setAiResponse(null)
    const runAi = async () => {
      try {
        let response
        if (action === 'generate') response = await generateJavaFromCobol(selectedText, 'workspace', sectionName)
        else if (action === 'explain') response = await explainCobolCode(selectedText, 'workspace', sectionName)
        else if (action === 'optimize') response = await optimizeJavaCode(selectedText, 'workspace', sectionName)
        else response = await explainCobolCode(selectedText, 'workspace', sectionName)
        setAiResponse(response)
      } catch (error) { setAiResponse({ error: error.message }) }
      finally { setAiLoading(false) }
    }
    runAi()
  }, [targetSelectedFile])

  // --- AI: register actions on Monaco editor mount ---
  const registerAIActions = useCallback((editor, monaco) => {
    editor.addAction({ id: 'ai-explain-code', label: 'AI: Explain This Code',
      keybindings: [monaco.KeyMod.CtrlCmd | monaco.KeyMod.Shift | monaco.KeyCode.KeyE],
      run: (ed) => handleEditorAiAction('explain', ed) })
    editor.addAction({ id: 'ai-generate-java', label: 'AI: Generate Java Code',
      keybindings: [monaco.KeyMod.CtrlCmd | monaco.KeyMod.Shift | monaco.KeyCode.KeyG],
      run: (ed) => handleEditorAiAction('generate', ed) })
    editor.addAction({ id: 'ai-refactor-code', label: 'AI: Suggest Refactoring',
      keybindings: [monaco.KeyMod.CtrlCmd | monaco.KeyMod.Shift | monaco.KeyCode.KeyR],
      run: (ed) => handleEditorAiAction('optimize', ed) })
    editor.addAction({ id: 'ai-custom-prompt', label: 'AI: Custom Prompt...',
      keybindings: [monaco.KeyMod.CtrlCmd | monaco.KeyMod.Shift | monaco.KeyCode.KeyA],
      run: (ed) => handleEditorAiAction('custom', ed) })
    // Slice-specific AI actions
    editor.addAction({ id: 'ai-slice-explain', label: 'AI Slice: Explain Source\u2192Target Mapping',
      keybindings: [monaco.KeyMod.CtrlCmd | monaco.KeyMod.Shift | monaco.KeyCode.KeyD],
      run: () => {
        if (!sliceState?.active) return
        const code = `// COBOL Source:\n${sliceState.source?.content || ''}\n\n// Java Target:\n${sliceState.target?.content || ''}`
        setAiModal({ isOpen: true, mode: 'explain', title: 'Slice Mapping Explanation', selectedCode: code, sectionName: sliceState.target?.anchorName || '' })
        setAiLoading(true); setAiResponse(null)
        explainCobolCode(code, 'slice-explain', sliceState.target?.anchorName || '')
          .then(r => setAiResponse(r)).catch(e => setAiResponse({ error: e.message })).finally(() => setAiLoading(false))
      } })
    editor.addAction({ id: 'ai-slice-nullchecks', label: 'AI Slice: Add Null Checks to Java',
      keybindings: [monaco.KeyMod.CtrlCmd | monaco.KeyMod.Shift | monaco.KeyCode.KeyN],
      run: () => {
        const code = sliceEditContent ?? sliceState?.target?.content ?? ''
        if (!code) return
        setAiModal({ isOpen: true, mode: 'generate', title: 'Add Null Checks', selectedCode: code, sectionName: sliceState?.target?.anchorName || '' })
        setAiLoading(true); setAiResponse(null)
        customPromptAI(code, 'Add defensive null checks to this Java code. Return the full updated code.', 'slice-nullchecks', sliceState?.target?.anchorName || '')
          .then(r => setAiResponse(r)).catch(e => setAiResponse({ error: e.message })).finally(() => setAiLoading(false))
      } })
    // AI Clean Code — calls the latest handler via ref so stale closure is not an issue
    editor.addAction({
      id: 'ai-clean-code',
      label: 'AI: Clean Code',
      keybindings: [monaco.KeyMod.CtrlCmd | monaco.KeyMod.Shift | monaco.KeyCode.KeyK],
      run: () => { handleCleanCodeRef.current?.() }
    })
  }, [handleEditorAiAction, sliceState, sliceEditContent])

  const handleCloseAiModal = useCallback(() => {
    setAiModal(prev => ({ ...prev, isOpen: false }))
    setAiResponse(null)
  }, [])

  const handleAcceptAICode = useCallback((code) => {
    if (!targetEditorRef.current || !code) return
    const editor = targetEditorRef.current
    const selection = editor.getSelection()
    const position = editor.getPosition()
    const range = (selection && !selection.isEmpty()) ? selection : { startLineNumber: position.lineNumber, startColumn: position.column, endLineNumber: position.lineNumber, endColumn: position.column }
    editor.executeEdits('ai-insert', [{ range, text: '\n' + code + '\n', forceMoveMarkers: true }])
    editor.focus()
    handleCloseAiModal()
  }, [handleCloseAiModal])

  // --- AI Clean Code: determine content, call backend, show diff preview ---
  const handleCleanCode = useCallback(async () => {
    // Determine what content to clean and the language
    let content, filename, language
    if (sliceViewMode === 'slice' && sliceState?.active && sliceState?.target) {
      content = sliceEditContent ?? sliceState.target.content ?? ''
      filename = sliceState.target.anchorName || targetSelectedFile?.path?.split('/').pop() || 'slice.java'
    } else {
      content = targetEditorRef.current?.getValue() ?? targetSelectedFile?.content ?? ''
      filename = targetSelectedFile?.path?.split('/').pop() || 'file.java'
    }
    const ext = filename.split('.').pop().toLowerCase()
    const langMap = { java: 'java', py: 'python', js: 'javascript', ts: 'typescript', cs: 'csharp', cpp: 'cpp' }
    language = langMap[ext] || 'java'

    if (!content.trim()) {
      setAiNotification('AI Clean Code: no content to clean.')
      setTimeout(() => setAiNotification(null), 3000)
      return
    }
    setCleanCodeLoading(true)
    try {
      const result = await cleanCode(content, language === 'java' ? 'Java' : language, filename)
      if (!result.cleaned || result.cleaned.trim() === content.trim()) {
        setAiNotification('AI Clean Code: no formatting changes detected.')
        setTimeout(() => setAiNotification(null), 3000)
        return
      }
      setCleanCodeModal({ isOpen: true, original: content, cleaned: result.cleaned, filename, language })
    } catch (err) {
      setAiNotification(`AI Clean Code failed: ${err.message}`)
      setTimeout(() => setAiNotification(null), 5000)
    } finally {
      setCleanCodeLoading(false)
    }
  }, [sliceViewMode, sliceState, sliceEditContent, targetSelectedFile])

  // Keep the ref current so the Monaco action always calls the latest handler
  handleCleanCodeRef.current = handleCleanCode

  // --- AI Clean Code: apply the cleaned content to the correct editor/state ---
  const handleApplyCleanCode = useCallback((cleaned) => {
    if (sliceViewMode === 'slice' && sliceState?.active && sliceState?.target) {
      // Slice mode: update slice edit state — "Apply Slice Edits" button will merge to full file
      setSliceEditContent(cleaned)
    } else {
      // Full-file mode: replace all content via executeEdits (preserves undo stack)
      const editor = targetEditorRef.current
      if (!editor) return
      const model = editor.getModel()
      if (!model) return
      const fullRange = model.getFullModelRange()
      editor.executeEdits('ai-clean-code', [{ range: fullRange, text: cleaned, forceMoveMarkers: true }])
      editor.focus()
      // onChange fires → handleTargetEditorChange → editedFiles + dirtyPaths updated
    }
    setCleanCodeModal(null)
  }, [sliceViewMode, sliceState])

  const handleRegenerate = useCallback(() => {
    if (!aiModal.selectedCode) return
    setAiLoading(true)
    setAiResponse(null)
    const runAi = async () => {
      try {
        let response
        if (aiModal.mode === 'generate') response = await generateJavaFromCobol(aiModal.selectedCode, 'regenerate', aiModal.sectionName)
        else if (aiModal.mode === 'explain') response = await explainCobolCode(aiModal.selectedCode, 'regenerate', aiModal.sectionName)
        else if (aiModal.mode === 'optimize') response = await optimizeJavaCode(aiModal.selectedCode, 'regenerate', aiModal.sectionName)
        else response = await explainCobolCode(aiModal.selectedCode, 'regenerate', aiModal.sectionName)
        setAiResponse(response)
      } catch (error) { setAiResponse({ error: error.message }) }
      finally { setAiLoading(false) }
    }
    runAi()
  }, [aiModal])

  const handleCustomPromptSubmit = useCallback((userPrompt) => {
    if (!aiModal.selectedCode || !userPrompt) return
    setAiLoading(true)
    setAiResponse(null)
    customPromptAI(aiModal.selectedCode, userPrompt, 'custom-prompt', aiModal.sectionName)
      .then(response => setAiResponse(response))
      .catch(error => setAiResponse({ error: error.message }))
      .finally(() => setAiLoading(false))
  }, [aiModal.selectedCode, aiModal.sectionName])

  // --- Slice workbench: activate slice from outline/traceability click ---
  const activateSlice = useCallback(({ outlineItem, traceabilityMapping } = {}) => {
    if (DEBUG_SLICE) console.log('[Slice] activateSlice called:', { outlineItem, traceabilityMapping, hasTarget: !!targetSelectedFile, sourceLen: sourceContent?.length || 0, splitMode })
    if (!targetSelectedFile || !targetSelectedFile.content) {
      console.warn('[Slice] No target file selected — cannot activate slice')
      setSliceBanner({ type: 'warning', message: 'No target file selected. Open a Java file first.' })
      return
    }
    const result = resolveSlice({
      targetPath: targetSelectedFile.path,
      targetContent: getEffectiveContent(targetSelectedFile.path),
      outlineItem: outlineItem || null,
      traceabilityMapping: traceabilityMapping || null,
      traceabilityData,
      sourceContent,
      sourcePath,
      analysisData,
    })
    if (DEBUG_SLICE) console.log('[Slice] resolveSlice result:', result ? { srcAnchor: result.source?.anchorName, srcLen: result.source?.content?.length, tgtAnchor: result.target?.anchorName, tgtLines: `${result.target?.startLine}-${result.target?.endLine}` } : 'null')
    if (result) {
      setSliceState(result)
      setSliceViewMode('slice')
      setSliceEditContent(null)
      setSliceBanner(null)
      if (!splitMode) setSplitMode(true)
      if (DEBUG_SLICE) console.log('[Slice] resolved:', {
        method: result.target?.anchorName,
        javaLines: `${result.target?.startLine}-${result.target?.endLine}`,
        cobolPara: result.source?.anchorName,
        cobolLines: result.source?.startLine ? `${result.source.startLine}-${result.source.endLine}` : 'full file',
      })
    } else {
      setSliceBanner({ type: 'warning', message: 'Could not resolve a code slice for this item. Try clicking a method or paragraph.' })
    }
  }, [targetSelectedFile, getEffectiveContent, traceabilityData, sourceContent, sourcePath, analysisData, splitMode])

  // --- Slice workbench: reset to full file, close split ---
  const resetSlice = useCallback(() => {
    setSliceState(null)
    setSliceViewMode('full')
    setSliceEditContent(null)
    setSliceBanner(null)
    setSplitMode(false)
  }, [])

  // --- Slice workbench: reset slice when switching files ---
  useEffect(() => {
    setSliceState(null)
    setSliceViewMode('full')
    setSliceEditContent(null)
    setSliceBanner(null)
  }, [activeTabPath])

  // --- Slice workbench: re-resolve when source content loads after activation ---
  useEffect(() => {
    if (sliceViewMode !== 'slice' || !sourceContent || !targetSelectedFile) return
    // Only re-resolve if source slice is empty (was activated before COBOL loaded)
    if (sliceState && (!sliceState.source?.content || sliceState.source.content === '')) {
      if (DEBUG_SLICE) console.log('[Slice] Re-resolving: source content now available, length=', sourceContent.length)
      const targetAnchor = sliceState.target
      if (!targetAnchor) return
      const result = resolveSlice({
        targetPath: targetSelectedFile.path,
        targetContent: getEffectiveContent(targetSelectedFile.path),
        outlineItem: { type: 'method', line: targetAnchor.startLine, name: targetAnchor.anchorName },
        traceabilityMapping: null,
        traceabilityData,
        sourceContent,
        sourcePath,
        analysisData,
      })
      if (result) {
        if (DEBUG_SLICE) console.log('[Slice] Re-resolved successfully:', result.source?.anchorName, '→', result.target?.anchorName)
        setSliceState(result)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceContent, sliceViewMode])

  // --- Slice workbench: persist slice state (debounced) ---
  useEffect(() => {
    if (!serviceGenJobId || !sliceState) return
    const timer = setTimeout(() => {
      try { localStorage.setItem(`ws_slice_${serviceGenJobId}`, JSON.stringify(sliceState)) } catch { /* non-fatal */ }
    }, 1000)
    return () => clearTimeout(timer)
  }, [sliceState, serviceGenJobId])

  // --- Slice workbench: restore slice state on mount ---
  useEffect(() => {
    if (!serviceGenJobId) return
    try {
      const saved = localStorage.getItem(`ws_slice_${serviceGenJobId}`)
      if (saved) setSliceState(JSON.parse(saved))
    } catch { /* non-fatal */ }
  }, [serviceGenJobId])

  // --- Persist tree expanded state per jobId ---
  useEffect(() => {
    if (!serviceGenJobId) return
    const key = `ws_target_tree_${serviceGenJobId}`
    try { localStorage.setItem(key, JSON.stringify(targetTreeExpanded)) } catch { /* non-fatal */ }
  }, [targetTreeExpanded, serviceGenJobId])

  // Restore tree expanded state when jobId changes
  useEffect(() => {
    if (!serviceGenJobId) return
    const key = `ws_target_tree_${serviceGenJobId}`
    try {
      const saved = localStorage.getItem(key)
      if (saved) setTargetTreeExpanded(JSON.parse(saved))
    } catch { /* non-fatal */ }
  }, [serviceGenJobId])

  const renderTargetTab = () => {
    // Empty state
    if (!serviceGenJobId) {
      return (
        <div className="ws-target-tab">
          <div className="ws-target-empty">
            <Package size={48} />
            <h3>No generated output yet</h3>
            <p>Run "Generate Service" from the Transformation Scope panel to generate Spring services from your COBOL analysis.</p>
          </div>
        </div>
      )
    }

    // Loading state
    if (targetLoading && Object.keys(targetFileMap).length === 0) {
      return (
        <div className="ws-target-tab">
          <div className="ws-target-empty">
            <Clock size={48} className="ws-target-spin" />
            <h3>Loading generated files...</h3>
          </div>
        </div>
      )
    }

    // Build semantic tree items from results + fileMap
    const allPaths = Object.keys(targetFileMap)
    const filter = targetTreeFilter.toLowerCase()

    // Source files from analysis
    const sourcePrograms = analysisData.programs
      .filter(p => !p.type || p.type === 'program')
      .map(p => ({ label: p.name, sublabel: 'COBOL', path: p.path, source: 'cobol' }))
    const sourceCopybooks = (analysisData.copybooks || [])
      .map(c => ({ label: c.name, sublabel: 'COPYBOOK', path: c.path, source: 'cobol' }))

    // Services: from API results (preferred) or file paths
    const serviceItems = targetResults?.services?.length
      ? targetResults.services.map(s => ({
          label: s.name,
          sublabel: `${s.method_count} methods`,
          path: allPaths.find(p => p.endsWith(s.filename)) || null,
        }))
      : allPaths
          .filter(p => p.includes('/services/') && p.endsWith('.java'))
          .map(p => ({ label: p.split('/').pop().replace('.java', ''), sublabel: null, path: p }))

    // Entities: from file paths
    const entityItems = allPaths
      .filter(p => p.includes('/entities/') && p.endsWith('.java'))
      .map(p => ({ label: p.split('/').pop().replace('.java', ''), sublabel: null, path: p }))

    // Reports
    const reportFiles = ['validation_report.json', 'traceability_map.json']
    const reportItems = reportFiles
      .map(name => {
        const path = allPaths.find(p => p.endsWith(name))
        return path ? { label: name.replace('.json', '').replace(/_/g, ' '), sublabel: null, path } : null
      })
      .filter(Boolean)

    // Indexes
    const indexFiles = ['manifest.json', 'method_index.json', 'service_summary.json', 'service_catalog.json', 'entity_catalog.json']
    const indexItems = indexFiles
      .map(name => {
        const path = allPaths.find(p => p.endsWith(name))
        return path ? { label: name.replace('.json', '').replace(/_/g, ' '), sublabel: null, path } : null
      })
      .filter(Boolean)

    // Apply filter
    const filterItems = (items) => filter
      ? items.filter(i => i.label.toLowerCase().includes(filter))
      : items

    const filteredSourceProgs = filterItems(sourcePrograms)
    const filteredSourceCopy = filterItems(sourceCopybooks)
    const filteredServices = filterItems(serviceItems)
    const filteredEntities = filterItems(entityItems)
    const filteredReports = filterItems(reportItems)
    const filteredIndexes = filterItems(indexItems)

    const fileCount = allPaths.length
    const r = targetResults

    const renderSection = (key, label, icon, items) => {
      if (items.length === 0 && !targetTreeExpanded[key]) return null
      return (
        <div className="ws-target-tree-section" key={key}>
          <div
            className="ws-target-tree-section-header"
            onClick={() => toggleTargetSection(key)}
          >
            {targetTreeExpanded[key] ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            {icon}
            <span className="ws-target-tree-section-label">{label}</span>
            <span className="ws-target-tree-section-count">{items.length}</span>
          </div>
          {targetTreeExpanded[key] && items.map(item => (
            <div
              key={item.path || item.label}
              className={`ws-target-tree-item ${activeTabPath === item.path ? 'selected' : ''} ${!item.path ? 'disabled' : ''}`}
              onClick={() => item.path && openFileInTab(item.path, item.source || 'target')}
            >
              <FileCode size={13} />
              <span className="ws-target-tree-item-label">{item.label}</span>
              {item.sublabel && <span className="ws-target-tree-item-sub">{item.sublabel}</span>}
            </div>
          ))}
        </div>
      )
    }

    // Outline icon helper
    const outlineIcon = (type) => {
      switch (type) {
        case 'class': return <Box size={12} />
        case 'interface': return <Layers size={12} />
        case 'method': return <Zap size={12} />
        case 'division': return <FolderOpen size={12} />
        case 'section': return <Layers size={12} />
        case 'paragraph': return <Circle size={8} />
        default: return <Braces size={12} />
      }
    }

    // --- Determine if current file is a target file (editable) ---
    const isTargetFile = targetSelectedFile && openTabs.find(t => t.path === activeTabPath)?.source !== 'cobol'
    const isCurrentFileDirty = targetSelectedFile && dirtyPaths.has(targetSelectedFile.path)

    // --- Render right context panel content based on active tab ---
    const renderContextPanelContent = () => {
      if (targetContextTab === 'outline') {
        const outlineLabel = splitMode && activeEditor === 'source' && sourceContent
          ? `Source: ${sourcePath?.split('/').pop() || 'Source'}`
          : null
        return (
          <div className="ws-target-outline-list">
            {outlineLabel && <div className="ws-ctx-outline-label">{outlineLabel}</div>}
            {targetOutlineItems.length > 0 ? targetOutlineItems.map((item, idx) => (
              <div
                key={idx}
                className={`ws-target-outline-item ${item.type}`}
                onClick={() => {
                  // Dispatch to active editor
                  const editorRef = (splitMode && activeEditor === 'source')
                    ? sourceEditorRef : targetEditorRef
                  if (editorRef.current && item.line) {
                    editorRef.current.revealLineInCenter(item.line)
                    editorRef.current.setSelection({
                      startLineNumber: item.line, startColumn: 1,
                      endLineNumber: item.line, endColumn: 1,
                    })
                    editorRef.current.focus()
                  }
                  // Activate slice for method/paragraph clicks
                  if (item.type === 'method' || item.type === 'paragraph') {
                    activateSlice({ outlineItem: item })
                  }
                }}
              >
                {outlineIcon(item.type)}
                <span className="ws-target-outline-name">{item.name}</span>
                <span className="ws-target-outline-line">:{item.line}</span>
              </div>
            )) : (
              <div className="ws-ctx-empty">
                {targetSelectedFile ? 'No symbols found' : 'Open a file to see its outline'}
              </div>
            )}
          </div>
        )
      }

      if (targetContextTab === 'issues') {
        if (!issuesData) {
          return (
            <div className="ws-ctx-empty">
              <Info size={16} />
              <span>No validation_report.json found in generated output.</span>
            </div>
          )
        }
        return (
          <div className="ws-ctx-issues">
            <div className="ws-ctx-issues-summary">
              <div className={`ws-ctx-issues-badge ${issuesData.errorCount > 0 ? 'error' : ''}`}>
                <XCircle size={12} />
                <span>{issuesData.errorCount} errors</span>
              </div>
              <div className={`ws-ctx-issues-badge ${issuesData.warningCount > 0 ? 'warning' : ''}`}>
                <AlertTriangle size={12} />
                <span>{issuesData.warningCount} warnings</span>
              </div>
              <div className="ws-ctx-issues-badge pass">
                <CheckCircle size={12} />
                <span>{issuesData.passedCount} passed</span>
              </div>
            </div>
            <div className="ws-ctx-issues-list">
              {issuesData.checks.map((check, idx) => {
                const severity = check.severity || (check.status === 'FAIL' ? 'error' : check.status === 'WARN' ? 'warning' : 'info')
                return (
                  <div
                    key={idx}
                    className={`ws-ctx-issues-item ${severity}`}
                    onClick={() => {
                      if (issuesData.reportPath) openFileInTab(issuesData.reportPath, 'target')
                    }}
                  >
                    {severity === 'error' ? <XCircle size={12} /> : severity === 'warning' ? <AlertTriangle size={12} /> : <CheckCircle size={12} />}
                    <span className="ws-ctx-issues-item-name">{check.name || check.check || `Check ${idx + 1}`}</span>
                    {check.message && <span className="ws-ctx-issues-item-msg">{check.message}</span>}
                  </div>
                )
              })}
            </div>
          </div>
        )
      }

      if (targetContextTab === 'traceability') {
        if (!traceabilityData) {
          return (
            <div className="ws-ctx-empty">
              <Info size={16} />
              <span>No traceability_map.json found in generated output.</span>
            </div>
          )
        }
        const searchLower = traceabilitySearch.toLowerCase()
        const filteredMappings = searchLower
          ? traceabilityData.mappings.filter(m => {
              const src = (m.source || m.cobol_name || m.cobol_paragraph || '').toLowerCase()
              const tgt = (m.target || m.java_method || m.java_class || '').toLowerCase()
              return src.includes(searchLower) || tgt.includes(searchLower)
            })
          : traceabilityData.mappings
        return (
          <div className="ws-ctx-trace">
            <div className="ws-ctx-trace-search">
              <Search size={12} />
              <input
                type="text"
                placeholder="Search mappings..."
                value={traceabilitySearch}
                onChange={(e) => setTraceabilitySearch(e.target.value)}
              />
              {traceabilitySearch && (
                <button className="ws-ctx-trace-search-clear" onClick={() => setTraceabilitySearch('')}>
                  <X size={10} />
                </button>
              )}
            </div>
            <div className="ws-ctx-trace-count">{filteredMappings.length} mapping{filteredMappings.length !== 1 ? 's' : ''}</div>
            <div className="ws-ctx-trace-list">
              {filteredMappings.map((m, idx) => {
                const source = m.source || m.cobol_name || m.cobol_paragraph || 'Unknown source'
                const target = m.target || m.java_method || m.java_class || 'Unknown target'
                const targetFile = m.target_file || m.java_file || null
                return (
                  <div
                    key={idx}
                    className="ws-ctx-trace-item"
                    onClick={() => {
                      if (targetFile) {
                        const matchPath = allPaths.find(p => p.endsWith(targetFile))
                        if (matchPath) {
                          openFileInTab(matchPath, 'target')
                          // Activate split mode + slice for this mapping
                          setSplitMode(true)
                          activateSlice({ traceabilityMapping: m })
                          if (m.line && targetEditorRef.current) {
                            setTimeout(() => targetEditorRef.current.revealLineInCenter(m.line), 300)
                          }
                        }
                      } else if (traceabilityData.mapPath) {
                        openFileInTab(traceabilityData.mapPath, 'target')
                      }
                    }}
                  >
                    <div className="ws-ctx-trace-mapping">
                      <span className="ws-ctx-trace-src">{source}</span>
                      <ArrowRight size={10} className="ws-ctx-trace-arrow" />
                      <span className="ws-ctx-trace-tgt">{target}</span>
                    </div>
                    {targetFile && <span className="ws-ctx-trace-file"><Link2 size={10} /> {targetFile}</span>}
                  </div>
                )
              })}
              {filteredMappings.length === 0 && (
                <div className="ws-ctx-empty">No mappings match your search.</div>
              )}
            </div>
          </div>
        )
      }
      return null
    }

    // --- Shared Monaco editor options ---
    const monacoOptions = {
      minimap: { enabled: true },
      fontSize: 13,
      fontFamily: "'JetBrains Mono', 'Fira Code', Consolas, monospace",
      automaticLayout: true,
      folding: true,
      scrollBeyondLastLine: false,
      renderLineHighlight: 'all',
      lineNumbers: 'on',
    }

    return (
      <div className="ws-target-tab">
        {/* Header bar */}
        <div className="ws-target-header">
          <div className="ws-target-title">
            <Package size={16} />
            <span>Target</span>
            <span className="ws-target-file-count">{fileCount} files</span>
            {dirtyPaths.size > 0 && (
              <span className="ws-target-badge dirty">{dirtyPaths.size} modified</span>
            )}
            {r && (
              <span className={`ws-target-badge ${r.validation_passed ? 'pass' : 'fail'}`}>
                {r.validation_passed ? <Check size={12} /> : <XCircle size={12} />}
                {r.validation_passed ? 'Valid' : 'Issues'}
              </span>
            )}
          </div>
          {r && (
            <div className="ws-target-stats">
              <span className="ws-target-stat"><Layers size={12} /> {r.service_count} services</span>
              <span className="ws-target-stat"><Zap size={12} /> {r.method_count} methods</span>
              <span className="ws-target-stat"><Database size={12} /> {r.entity_count} entities</span>
            </div>
          )}
          <div className="ws-target-actions">
            <span className="ws-target-jobid" title={serviceGenJobId}>{serviceGenJobId.slice(0, 12)}...</span>
            {dirtyPaths.size > 0 && (
              <button className="ws-target-download-btn ws-target-export-btn" onClick={handleExportWithEdits} disabled={isLoading}>
                <Download size={14} />
                Export with Edits
              </button>
            )}
            <button className="ws-target-download-btn" onClick={handleDownloadService} disabled={isLoading}>
              <Download size={14} />
              Download Original
            </button>
          </div>
        </div>

        {/* 3-pane IDE layout: Explorer | Editor | Context Panel */}
        <div className="ws-target-body">
          <Allotment defaultSizes={[220, 600, 180]}>
            {/* Pane 1: Project Explorer */}
            <Allotment.Pane minSize={180} maxSize={350}>
              <div className="ws-target-tree">
                <div className="ws-target-tree-search">
                  <Search size={13} />
                  <input
                    type="text"
                    placeholder="Filter files..."
                    value={targetTreeFilter}
                    onChange={(e) => setTargetTreeFilter(e.target.value)}
                  />
                  {targetTreeFilter && (
                    <button className="ws-target-tree-search-clear" onClick={() => setTargetTreeFilter('')}>
                      <X size={12} />
                    </button>
                  )}
                </div>
                {filteredSourceProgs.length > 0 && renderSection('source_programs', 'Source Programs', <FileCode size={14} />, filteredSourceProgs)}
                {filteredSourceCopy.length > 0 && renderSection('source_copybooks', 'Copybooks', <Code2 size={14} />, filteredSourceCopy)}
                {renderSection('services', 'Services', <Layers size={14} />, filteredServices)}
                {renderSection('entities', 'Entities', <Database size={14} />, filteredEntities)}
                {renderSection('reports', 'Reports', <FileText size={14} />, filteredReports)}
                {renderSection('indexes', 'Indexes', <Archive size={14} />, filteredIndexes)}
              </div>
            </Allotment.Pane>

            {/* Pane 2: Editor with tabs + breadcrumb + toolbar + split */}
            <Allotment.Pane minSize={300}>
              <div className="ws-target-editor">
                {/* Tab bar with dirty dots */}
                {openTabs.length > 0 && (
                  <div className="ws-target-tab-bar">
                    {openTabs.map(tab => (
                      <div
                        key={tab.path}
                        className={`ws-target-file-tab ${activeTabPath === tab.path ? 'active' : ''}`}
                        onClick={() => switchTab(tab.path)}
                      >
                        <FileCode size={12} />
                        <span className="ws-target-file-tab-label">{tab.label}</span>
                        {dirtyPaths.has(tab.path) && <span className="ws-dirty-dot" />}
                        <button
                          className="ws-target-file-tab-close"
                          onClick={(e) => closeTab(tab.path, e)}
                        >
                          <X size={12} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Breadcrumb + actions toolbar */}
                {targetSelectedFile && (
                  <div className="ws-target-editor-toolbar">
                    {/* Slice mode toggle */}
                    <div className="ws-slice-mode-toggle">
                      <button
                        className={`ws-slice-mode-btn ${sliceViewMode === 'full' ? 'active' : ''}`}
                        onClick={() => { setSliceViewMode('full'); setSliceEditContent(null) }}
                      >Full File</button>
                      <button
                        className={`ws-slice-mode-btn ${sliceViewMode === 'slice' ? 'active' : ''}`}
                        onClick={() => { if (sliceState) { setSliceViewMode('slice'); setSplitMode(true) } }}
                        disabled={!sliceState}
                      >Slice</button>
                    </div>
                    {/* Breadcrumb: slice or file */}
                    {sliceViewMode === 'slice' && sliceState ? (
                      <div className="ws-slice-breadcrumb">
                        <span className="ws-slice-crumb source">{sliceState.source?.anchorName || sourcePath?.split('/').pop() || 'Source'}</span>
                        <ArrowRight size={10} />
                        <span className="ws-slice-crumb target">{sliceState.target?.anchorName || targetSelectedFile.path.split('/').pop()}</span>
                        {sliceEditContent !== null && <span className="ws-dirty-indicator">Modified</span>}
                      </div>
                    ) : (
                      <div className="ws-target-breadcrumb">
                        {targetSelectedFile.path.split('/').map((seg, i, arr) => (
                          <span key={i}>
                            {i > 0 && <ChevronRight size={10} className="ws-breadcrumb-sep" />}
                            <span className={i === arr.length - 1 ? 'ws-breadcrumb-current' : 'ws-breadcrumb-segment'}>
                              {seg}
                            </span>
                          </span>
                        ))}
                        {isCurrentFileDirty && <span className="ws-dirty-indicator">Modified</span>}
                      </div>
                    )}
                    <div className="ws-target-editor-actions">
                      {/* Apply slice edits to full file */}
                      {sliceEditContent !== null && sliceState?.target && (
                        <button
                          className="ws-target-editor-action save"
                          onClick={() => {
                            const fullContent = getEffectiveContent(sliceState.target.filePath)
                            const merged = applySliceEdit(fullContent, sliceState.target.startLine, sliceState.target.endLine, sliceEditContent)
                            // Switch to full mode temporarily so handleTargetEditorChange sees the full file
                            const prevMode = sliceViewMode
                            setSliceViewMode('full')
                            handleTargetEditorChange(merged)
                            setSliceEditContent(null)
                            setSliceState(prev => prev ? { ...prev, target: { ...prev.target, content: sliceEditContent } } : prev)
                            // Restore slice mode
                            setTimeout(() => setSliceViewMode(prevMode), 0)
                          }}
                          title="Apply slice edits to full file"
                        >
                          <Check size={13} /> Apply
                        </button>
                      )}
                      {/* Discard slice edits */}
                      {sliceEditContent !== null && (
                        <button
                          className="ws-target-editor-action"
                          onClick={() => setSliceEditContent(null)}
                          title="Discard slice edits"
                        >
                          <RotateCcw size={13} />
                        </button>
                      )}
                      {/* Reset Slice — returns to Full File + closes split */}
                      {sliceViewMode === 'slice' && (
                        <button
                          className="ws-target-editor-action"
                          onClick={resetSlice}
                          title="Reset Slice — return to Full File"
                        >
                          <XCircle size={13} />
                        </button>
                      )}
                      {/* Split toggle */}
                      <button
                        className={`ws-target-editor-action ${splitMode ? 'active' : ''}`}
                        onClick={() => setSplitMode(prev => !prev)}
                        title={splitMode ? 'Single editor' : 'Split: Source | Target'}
                      >
                        <SplitSquareHorizontal size={13} />
                      </button>
                      {/* AI: Explain */}
                      <button
                        className="ws-target-editor-action ai"
                        onClick={() => {
                          if (targetEditorRef.current) handleEditorAiAction('explain', targetEditorRef.current)
                        }}
                        title="AI: Explain code (Cmd+Shift+E)"
                      >
                        <Sparkles size={13} />
                      </button>
                      {/* AI: Clean Code */}
                      <button
                        className={`ws-target-editor-action ai ${cleanCodeLoading ? 'loading' : ''}`}
                        onClick={handleCleanCode}
                        disabled={cleanCodeLoading}
                        title="AI: Clean Code — fix indentation and formatting (Cmd+Shift+K)"
                      >
                        {cleanCodeLoading
                          ? <Loader2 size={13} className="ws-spin" />
                          : <Wand2 size={13} />
                        }
                      </button>
                      {/* Save */}
                      {isCurrentFileDirty && (
                        <button
                          className="ws-target-editor-action save"
                          onClick={handleSaveEdits}
                          title="Save edits (persists to browser)"
                        >
                          <Save size={13} />
                        </button>
                      )}
                      {/* Revert */}
                      {isCurrentFileDirty && (
                        <button
                          className="ws-target-editor-action"
                          onClick={() => handleRevertFile(targetSelectedFile.path)}
                          title="Revert to original"
                        >
                          <RotateCcw size={13} />
                        </button>
                      )}
                      {/* Copy */}
                      <button className="ws-target-editor-action" onClick={handleCopyFileContent} title="Copy to clipboard">
                        {copiedToClipboard ? <ClipboardCheck size={13} /> : <Clipboard size={13} />}
                      </button>
                      {/* Open raw */}
                      <button className="ws-target-editor-action" onClick={handleOpenRaw} title="Open in new tab">
                        <ExternalLink size={13} />
                      </button>
                    </div>
                  </div>
                )}

                {/* Slice banner (shown when slice can't resolve) */}
                {sliceBanner && (
                  <div className={`ws-slice-banner ${sliceBanner.type}`}>
                    <AlertTriangle size={13} />
                    <span>{sliceBanner.message}</span>
                    <button onClick={() => setSliceBanner(null)}><X size={12} /></button>
                  </div>
                )}

                {/* Editor area: split or single, slice-aware */}
                <div className="ws-target-editor-area">
                {targetSelectedFile ? (
                  splitMode && (sourceContent || sliceViewMode === 'slice') ? (
                    <Allotment>
                      {/* Source pane (read-only) */}
                      <Allotment.Pane minSize={200}>
                        <div className="ws-split-editor-pane">
                          <div className="ws-split-editor-label source">
                            {sliceViewMode === 'slice' && sliceState?.source?.anchorName
                              ? `Source: ${sourcePath?.split('/').pop()} \u2192 ${sliceState.source.anchorName} (${sliceState.source.startLine}-${sliceState.source.endLine})`
                              : `Source: ${sourcePath?.split('/').pop() || 'No source found'}`}
                          </div>
                          <Editor
                            height="100%"
                            language={sourceLanguage}
                            value={
                              sliceViewMode === 'slice' && sliceState?.source?.content
                                ? sliceState.source.content
                                : sourceContent || '// Loading source file...'
                            }
                            theme="vs-dark"
                            onMount={(editor, monaco) => {
                              sourceEditorRef.current = editor
                              registerAIActions(editor, monaco)
                              editor.onDidFocusEditorWidget(() => setActiveEditor('source'))
                            }}
                            options={{ ...monacoOptions, readOnly: true }}
                          />
                        </div>
                      </Allotment.Pane>
                      {/* Target pane (editable) */}
                      <Allotment.Pane minSize={200}>
                        <div className="ws-split-editor-pane">
                          <div className="ws-split-editor-label target">
                            {sliceViewMode === 'slice' && sliceState?.target?.anchorName
                              ? `Target: ${targetSelectedFile.path.split('/').pop()} \u2192 ${sliceState.target.anchorName}() (${sliceState.target.startLine}-${sliceState.target.endLine})`
                              : `Target: ${targetSelectedFile.path.split('/').pop()}`}
                            {sliceViewMode === 'slice' && sliceEditContent !== null && <span className="ws-dirty-indicator">Modified</span>}
                            {sliceViewMode !== 'slice' && isCurrentFileDirty && <span className="ws-dirty-indicator">Modified</span>}
                          </div>
                          <Editor
                            height="100%"
                            language={targetSelectedFile.language}
                            value={sliceViewMode === 'slice' && sliceState?.target?.content
                              ? (sliceEditContent ?? sliceState.target.content)
                              : targetSelectedFile.content}
                            theme="vs-dark"
                            onChange={isTargetFile ? (newValue) => {
                              if (sliceViewMode === 'slice' && sliceState?.target) {
                                setSliceEditContent(newValue)
                              } else {
                                handleTargetEditorChange(newValue)
                              }
                            } : undefined}
                            onMount={(editor, monaco) => {
                              targetEditorRef.current = editor
                              registerAIActions(editor, monaco)
                              editor.onDidFocusEditorWidget(() => setActiveEditor('target'))
                            }}
                            options={{ ...monacoOptions, readOnly: !isTargetFile }}
                          />
                        </div>
                      </Allotment.Pane>
                    </Allotment>
                  ) : (
                    /* Single editor mode */
                    <Editor
                      height="100%"
                      language={targetSelectedFile.language}
                      value={sliceViewMode === 'slice' && sliceState?.target?.content
                        ? (sliceEditContent ?? sliceState.target.content)
                        : targetSelectedFile.content}
                      theme="vs-dark"
                      onChange={isTargetFile ? (newValue) => {
                        if (sliceViewMode === 'slice' && sliceState?.target) {
                          setSliceEditContent(newValue)
                        } else {
                          handleTargetEditorChange(newValue)
                        }
                      } : undefined}
                      onMount={(editor, monaco) => {
                        targetEditorRef.current = editor
                        registerAIActions(editor, monaco)
                      }}
                      options={{ ...monacoOptions, readOnly: !isTargetFile }}
                    />
                  )
                ) : (
                  <div className="ws-target-editor-placeholder">
                    <FileCode size={32} />
                    <span>Select a file from the explorer to view its contents</span>
                  </div>
                )}
                </div>
              </div>
            </Allotment.Pane>

            {/* Pane 3: Context Panel (Outline / Issues / Traceability) */}
            <Allotment.Pane minSize={160} maxSize={420}>
              <div className="ws-target-context">
                <div className="ws-ctx-tabs">
                  <button
                    className={`ws-ctx-tab ${targetContextTab === 'outline' ? 'active' : ''}`}
                    onClick={() => setTargetContextTab('outline')}
                  >
                    Outline
                    {targetOutlineItems.length > 0 && <span className="ws-ctx-tab-badge">{targetOutlineItems.length}</span>}
                  </button>
                  <button
                    className={`ws-ctx-tab ${targetContextTab === 'issues' ? 'active' : ''}`}
                    onClick={() => setTargetContextTab('issues')}
                  >
                    Issues
                    {issuesData && issuesData.errorCount > 0 && <span className="ws-ctx-tab-badge error">{issuesData.errorCount}</span>}
                  </button>
                  <button
                    className={`ws-ctx-tab ${targetContextTab === 'traceability' ? 'active' : ''}`}
                    onClick={() => setTargetContextTab('traceability')}
                  >
                    Traceability
                    {traceabilityData && <span className="ws-ctx-tab-badge">{traceabilityData.mappings.length}</span>}
                  </button>
                </div>
                <div className="ws-ctx-content">
                  {renderContextPanelContent()}
                </div>
              </div>
            </Allotment.Pane>
          </Allotment>
        </div>

        {/* AI Response Modal */}
        <AIResponseModal
          isOpen={aiModal.isOpen}
          onClose={handleCloseAiModal}
          title={aiModal.title}
          mode={aiModal.mode}
          selectedCode={aiModal.selectedCode}
          sectionName={aiModal.sectionName}
          aiResponse={aiResponse}
          aiLoading={aiLoading}
          onRegenerate={handleRegenerate}
          onAccept={handleAcceptAICode}
          onCustomPromptSubmit={handleCustomPromptSubmit}
        />
      </div>
    )
  }

  // ============================================================================
  // RENDER: MAIN GRAPH VIEW
  // ============================================================================

  const renderGraph = () => (
    <div className="ws-graph-wrapper">
      {/* Left Panel - Code Explorer */}
      <div className={`panel-container left-panel ${leftPanelCollapsed ? 'collapsed' : ''}`}>
        {!leftPanelCollapsed && (
          <div className="ws-explorer-panel">
            <CodeExplorer
              analysisData={analysisData}
              selectedNodeId={selectedNode?.id}
              onNodeSelect={handleExplorerNodeSelect}
              onNodeContextMenu={onNodeContextMenu}
              onUpload={() => setShowUploadModal(true)}
              onAnalysis={() => setShowAnalysisModal(true)}
            />
          </div>
        )}
        <button
          className="panel-toggle left-toggle"
          onClick={() => setLeftPanelCollapsed(!leftPanelCollapsed)}
          title={leftPanelCollapsed ? 'Show Code Explorer' : 'Hide Code Explorer'}
        >
          {leftPanelCollapsed ? <PanelLeftOpen size={16} /> : <PanelLeftClose size={16} />}
        </button>
      </div>

      {/* Center - Graph Canvas */}
      <div className="ws-graph-canvas">
        <ReactFlow
          nodes={filteredNodes}
          edges={filteredEdges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onNodeClick={onNodeClick}
          onPaneClick={onPaneClick}
          onNodeContextMenu={onNodeContextMenu}
          onInit={onInit}
          nodeTypes={nodeTypes}
          fitView
          fitViewOptions={{ padding: 0.25 }}
          proOptions={{ hideAttribution: true }}
          selectionOnDrag
          selectionMode="partial"
        >
          <Background gap={24} size={1} />
          <MiniMap pannable zoomable />
          <Controls />
        </ReactFlow>

        {/* Context Menu */}
        {contextMenu && (
          <>
            <div className="ws-context-menu-overlay" onClick={() => setContextMenu(null)} />
            <div className="ws-context-menu" style={{ top: contextMenu.y, left: contextMenu.x }}>
              <div className="ws-context-menu-header">
                {contextMenu.node?.data?.name || 'Node'}
              </div>
              {contextMenu.items.map((item, idx) => (
                item.key?.startsWith('divider') ? (
                  <div key={item.key || idx} className="ws-context-menu-divider" />
                ) : (
                  <button
                    key={item.key}
                    className={`ws-context-menu-item ${!item.enabled ? 'disabled' : ''} ${item.highlight ? 'highlight' : ''} ${item.success ? 'success' : ''} ${item.danger ? 'danger' : ''}`}
                    onClick={() => item.enabled && handleMenuAction(item.key, contextMenu.node)}
                    disabled={!item.enabled}
                  >
                    {item.icon}
                    {item.label}
                  </button>
                )
              ))}
            </div>
          </>
        )}
      </div>

      {/* Right Panel - Tabbed (Properties / Slice / Runs / AI) */}
      <div className={`panel-container right-panel ${rightPanelCollapsed ? 'collapsed' : ''}`}>
        <button
          className="panel-toggle right-toggle"
          onClick={() => setRightPanelCollapsed(!rightPanelCollapsed)}
          title={rightPanelCollapsed ? 'Show Panel' : 'Hide Panel'}
        >
          {rightPanelCollapsed ? <PanelRightOpen size={16} /> : <PanelRightClose size={16} />}
        </button>
        {!rightPanelCollapsed && (
          <aside className="ws-properties-dock">
            <div className="ws-right-panel-tabs">
              <button
                className={`ws-right-tab ${rightPanelTab === 'properties' ? 'active' : ''}`}
                onClick={() => setRightPanelTab('properties')}
                title="Properties"
              >
                <Search size={16} />
              </button>
              <button
                className={`ws-right-tab ${rightPanelTab === 'slice' ? 'active' : ''}`}
                onClick={() => setRightPanelTab('slice')}
                title="Scope"
              >
                <Boxes size={16} />
                {currentSlice.length > 0 && (
                  <span className="ws-right-tab-badge">{currentSlice.length}</span>
                )}
              </button>
              <button
                className={`ws-right-tab ${rightPanelTab === 'runs' ? 'active' : ''}`}
                onClick={() => setRightPanelTab('runs')}
                title="Saved Runs"
              >
                <Clock size={16} />
              </button>
              <button
                className={`ws-right-tab ${rightPanelTab === 'ai' ? 'active' : ''}`}
                onClick={() => setRightPanelTab('ai')}
                title="AI Naming"
              >
                <Sparkles size={16} />
              </button>
            </div>

            <div className="ws-right-panel-content">
              {rightPanelTab === 'properties' && renderPropertiesPanel()}
              {rightPanelTab === 'slice' && renderSlicePanel()}
              {rightPanelTab === 'runs' && renderSavedRunsPanel()}
              {rightPanelTab === 'ai' && renderAiNamingPanel()}
            </div>
          </aside>
        )}
      </div>
    </div>
  )

  // ============================================================================
  // RENDER: WORKBOOK TAB
  // ============================================================================

  // Get paragraphs from selected program (from real API data)
  const selectedProgramParagraphs = useMemo(() => {
    if (!selectedNode || selectedNode.data?.componentType !== 'program') return []
    const programName = selectedNode.data?.name || selectedNode.data?.label
    return analysisData.paragraphs[programName] || []
  }, [selectedNode, analysisData.paragraphs])

  const renderWorkbook = () => (
    <div className="ws-workbook-container">
      <div className="ws-workbook-left">
        <div className="ws-workbook-nav">
          <button className="ws-workbook-nav-tab active">Workbook</button>
          <button className="ws-workbook-nav-tab" onClick={() => setActiveTab('source')}>Source</button>
          <button className="ws-workbook-nav-tab" onClick={() => setActiveTab('target')}>Target</button>
        </div>
        <h3>Workbook details</h3>
        <p className="ws-workbook-desc">
          A workbook is a collection of service code blocks. Select paragraphs from the list to create a workbook for conversion.
        </p>
        <div className="ws-workbook-instructions">
          <ol>
            <li>Select a program in the Graph view</li>
            <li>Review key paragraphs ranked by importance</li>
            <li>Click "Add to Workbook" to define conversion scope</li>
          </ol>
        </div>
        <button className="ws-import-btn">
          <Upload size={14} /> Import workbook
        </button>
      </div>

      <div className="ws-workbook-middle">
        <h4>Key conditions</h4>
        {selectedProgramParagraphs.length > 0 ? (
          <>
            <p className="ws-condition-count">
              {selectedProgramParagraphs.length} paragraphs ordered by importance
            </p>
            <div className="ws-paragraph-list">
              {selectedProgramParagraphs.map(para => (
                <div
                  key={para.name}
                  className={`ws-paragraph-item ${selectedParagraph?.name === para.name ? 'selected' : ''}`}
                  onClick={() => setSelectedParagraph(para)}
                >
                  <div className="ws-para-info">
                    <span className="ws-para-name">{para.name}</span>
                    <span className="ws-para-line">(line {para.source_span?.line_start || '?'})</span>
                  </div>
                  <div className="ws-para-score">{Math.round((para.score || 0) * 100)}%</div>
                </div>
              ))}
            </div>
          </>
        ) : (
          <div className="ws-workbook-empty">
            <FileCode size={24} />
            <p>Select a program in the Graph view</p>
            <span>Run Analyze first if graph is empty</span>
          </div>
        )}
      </div>

      <div className="ws-workbook-right">
        {selectedNode?.data?.componentType === 'program' ? (
          <>
            <div className="ws-code-header">
              <span>{selectedNode.data?.name || selectedNode.data?.label}</span>
              <button className="ws-close-btn" onClick={() => setSelectedNode(null)}><X size={14} /></button>
            </div>
            <div className="ws-code-viewer">
              <pre className="ws-code"><code>{selectedNode.data?.source_code || '// Source code not loaded\n// Run analysis to load program details'}</code></pre>
              {selectedParagraph && (
                <button
                  className="ws-slice-btn"
                  onClick={async () => {
                    try {
                      const item = {
                        id: `${selectedNode.data.name}:${selectedParagraph.name}`,
                        name: selectedParagraph.name,
                        type: 'paragraph',
                        programName: selectedNode.data.name,
                        lineStart: selectedParagraph.source_span?.line_start,
                        lineEnd: selectedParagraph.source_span?.line_end
                      }

                      // Check if item already in slice
                      if (currentSlice.some(s => s.id === item.id)) {
                        setRightPanelTab('slice')
                        setRightPanelCollapsed(false)
                        setActiveTab('source')
                        return
                      }

                      // Try to create workbook if none exists (optional - falls back to local-only)
                      let workbook = currentWorkbook
                      if (!workbook && hasRealProject) {
                        try {
                          const user = JSON.parse(localStorage.getItem('modernizeit_user') || '{}')
                          if (user.user_id) {
                            const newWb = await workspaceService.createWorkbook(
                              project.accountId,
                              user.user_id,
                              `${project.applicationName} Workbook`,
                              'Incremental transformation workbook'
                            )
                            workbook = newWb
                            setCurrentWorkbook(newWb)
                            setWorkbooks(prev => [...prev, newWb])
                            localStorage.setItem(`workspace_currentWorkbook_${project.accountId}`, newWb.workbook_id)
                          }
                        } catch (apiError) {
                          console.warn('[WorkspaceView] Failed to create workbook in database (using local-only mode):', apiError)
                          // Continue without workbook - local state only
                        }
                      }

                      // Try to add item to workbook via API (optional)
                      if (workbook) {
                        try {
                          const dbItem = await workspaceService.addWorkbookItem(workbook.workbook_id, {
                            program_name: item.programName,
                            paragraph_name: item.name,
                            paragraph_id: item.id,
                            line_start: item.lineStart,
                            line_end: item.lineEnd
                          })
                          item.itemId = dbItem.item_id
                          item.status = dbItem.status
                        } catch (apiError) {
                          console.warn('[WorkspaceView] Failed to save item to database (using local-only mode):', apiError)
                          // Continue without database persistence - local state only
                        }
                      }

                      // Update local state (always works, even if API fails)
                      setCurrentSlice(prev => [...prev, item])
                      setRightPanelTab('slice')
                      setRightPanelCollapsed(false)
                      setActiveTab('source')
                    } catch (error) {
                      console.error('[WorkspaceView] Failed to add paragraph:', error)
                      // Don't show alert - just log and continue in local-only mode
                      // Update local state anyway
                      const item = {
                        id: `${selectedNode.data.name}:${selectedParagraph.name}`,
                        name: selectedParagraph.name,
                        type: 'paragraph',
                        programName: selectedNode.data.name,
                        lineStart: selectedParagraph.source_span?.line_start,
                        lineEnd: selectedParagraph.source_span?.line_end
                      }
                      setCurrentSlice(prev => {
                        if (prev.some(s => s.id === item.id)) return prev
                        return [...prev, item]
                      })
                      setRightPanelTab('slice')
                      setRightPanelCollapsed(false)
                      setActiveTab('source')
                    }
                  }}
                >
                  Add paragraph to workbook
                </button>
              )}
            </div>
          </>
        ) : (
          <div className="ws-workbook-empty">
            <Monitor size={24} />
            <p>No program selected</p>
            <span>Select a program from the Graph view to see its source code</span>
          </div>
        )}
      </div>
    </div>
  )

  // ============================================================================
  // MAIN RENDER
  // ============================================================================

  return (
    <div className="workspace-view">
      {/* Header */}
      <div className="ws-header">
        <div className="ws-header-left">
          <span className="ws-app-name">{applicationName || 'GenAppSD'}</span>
          <div className="ws-search">
            <Search size={14} />
            <input
              type="text"
              placeholder="Search components..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button className="ws-search-clear" onClick={() => setSearchQuery('')}>
                <X size={14} />
              </button>
            )}
          </div>
        </div>
        <div className="ws-header-right">
          <div className="ws-tabs">
            <button
              className={`ws-tab ${activeTab === 'source' ? 'active' : ''}`}
              onClick={() => setActiveTab('source')}
            >
              Source
            </button>
            <button
              className={`ws-tab ${activeTab === 'target' ? 'active' : ''}`}
              onClick={() => setActiveTab('target')}
            >
              Target
            </button>
            <button
              className={`ws-tab ${activeTab === 'workbook' ? 'active' : ''}`}
              onClick={() => setActiveTab('workbook')}
            >
              Workbook
            </button>
          </div>
        </div>
      </div>

      {/* Action Toolbar */}
      {activeTab !== 'workbook' && renderActionToolbar()}

      {/* Main Content */}
      <div className="ws-main">
        {activeTab === 'workbook' ? renderWorkbook() : activeTab === 'target' ? renderTargetTab() : renderGraph()}
      </div>

      {/* Modals */}
      {renderUploadModal()}
      {renderServiceModal()}
      {renderRunFlowModal()}
      {showRefactorPanel && renderRefactorPanel()}

      {/* AI Clean Code: diff preview modal */}
      {cleanCodeModal?.isOpen && (
        <CleanCodeModal
          original={cleanCodeModal.original}
          cleaned={cleanCodeModal.cleaned}
          filename={cleanCodeModal.filename}
          language={cleanCodeModal.language}
          onApply={handleApplyCleanCode}
          onClose={() => setCleanCodeModal(null)}
        />
      )}

      {/* AI notification toast */}
      {aiNotification && (
        <div className="ws-ai-notification">
          <Sparkles size={13} />
          <span>{aiNotification}</span>
          <button onClick={() => setAiNotification(null)}><X size={12} /></button>
        </div>
      )}
    </div>
  )
}
